import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { createRoot } from "react-dom/client";
import Editor from "@monaco-editor/react";
import Prism from "prismjs";
import "prismjs/components/prism-json";
import "prismjs/components/prism-bash";
import prismStyles from "prismjs/themes/prism-tomorrow.min.css?inline";
import styles from "./index.scss?inline";
import { migrateSchemaToShortForm, parseTvLinkLine, TV_SCHEMA_MARKER } from "./utils.js";
import { getFocusableNavLinks, initTvMode } from "./tv-mode.js";
import { TvModeToggle, useTvMode } from "./tv-mode-toggle.jsx";

// Inject styles inline into the document head
const styleEl = document.createElement("style");
styleEl.textContent = prismStyles + "\n" + styles;
document.head.appendChild(styleEl);

// Apply the host page preference before any route or async schema can render.
initTvMode();

// Set global flag if script URL has ?hasCustomNavBeforeLoad=1
const params = new URLSearchParams(document.currentScript?.src.split("?")[1]);
window.hasCustomNavBeforeLoad = params.get("hasCustomNavBeforeLoad") === "1";

const isRenderedInDataUrl = location.href.indexOf("data:") === 0;

// Disable Prism auto-highlighting
Prism.manual = true;

/**
 * Detects the programming language of a code snippet for syntax highlighting.
 * @param {string} code - The code string to analyze.
 * @returns {string} The detected language: "json", "javascript", or "bash".
 */
function _detectCodeLanguage(code) {
  const trimmed = code.trim();
  // Try JSON
  if (/^\s*[{[]/.test(trimmed)) {
    try {
      JSON.parse(trimmed);
      return "json";
    } catch {}
  }
  // Try JS-like patterns: const/let/var/function/=>/import/export/class
  if (/(?:^|\n)\s*(?:const |let |var |function |import |export |class |=>|\/\/)/.test(trimmed)) {
    return "javascript";
  }
  // Default to bash
  return "bash";
}

/**
 * Highlights code using Prism.js with auto-detected language.
 * @param {string} code - The code string to highlight.
 * @returns {string} HTML string with syntax highlighting markup.
 */
function _highlightCode(code) {
  const lang = _detectCodeLanguage(code);
  const grammar = Prism.languages[lang];
  if (!grammar) return code;
  return Prism.highlight(code, grammar, lang);
}

const APP_UPSTREAM_DEFAULT_BASE_URL = "https://synle.github.io/nav-generator";
const APP_BASE_URL =
  isRenderedInDataUrl || window.hasCustomNavBeforeLoad
    ? APP_UPSTREAM_DEFAULT_BASE_URL
    : location.href.substr(0, location.href.lastIndexOf("/")); // this is the base url
const APP_INDEX_URL = `${APP_BASE_URL}/index.html`;
const NEW_NAV_URL = `${APP_INDEX_URL}?newNav`;

// custom events
window.copyToClipboard = async (text) => {
  text = (text || "").trim();
  if (text) {
    try {
      await navigator.clipboard.writeText(text);
      await alert("Copied to clipboard!");
    } catch {
      await prompt("Clipboard", text);
    }
  }
};
document.addEventListener("AppCopyTextToClipboard", (e) => window.copyToClipboard(e.text));

/**
 * Modal component that renders a portal overlay with backdrop.
 * Supports closing via Escape key or clicking outside.
 * @param {Object} props
 * @param {boolean} props.isOpen - Whether the modal is visible.
 * @param {Function} props.onClose - Callback when the modal should close.
 * @param {React.ReactNode} props.children - Modal content.
 * @returns {React.ReactPortal|null}
 */
function Modal(props) {
  const { isOpen, onClose, children } = props;
  const modalRef = useRef(null);

  useLayoutEffect(() => {
    if (!isOpen) return;

    const handleEscape = (e) => {
      if (e.key === "Escape") {
        onClose();
      }
    };

    const handleClickOutside = (e) => {
      if (modalRef.current && e.target === modalRef.current) {
        onClose();
      }
    };

    document.addEventListener("keydown", handleEscape);
    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("keydown", handleEscape);
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return createPortal(
    <div className="modal" ref={modalRef}>
      <div className="modalContent">{children}</div>
    </div>,
    document.body,
  );
}

/**
 * Alert/Confirm modal component. Renders OK button for alerts, Yes/No for confirms.
 * @param {Object} props
 * @param {string} props.message - The message to display.
 * @param {Function} props.onClose - Callback with confirmation result (boolean for confirm, void for alert).
 * @param {"alert"|"confirm"} [props.type="alert"] - Modal type.
 * @returns {JSX.Element}
 */
function AlertModal(props) {
  const { message, onClose, type = "alert" } = props;
  const primaryButtonRef = useRef(null);

  useLayoutEffect(() => {
    if (primaryButtonRef.current) {
      primaryButtonRef.current.focus();
    }
  }, []);

  if (type === "confirm") {
    return (
      <Modal isOpen={true} onClose={() => onClose(false)}>
        <div className="modalBody">
          <div className="modalMessage">{message}</div>
          <footer className="modalFooter">
            <button
              ref={primaryButtonRef}
              type="button"
              className="modalBtn primary"
              onClick={() => onClose(true)}
            >
              Yes
            </button>
            <button
              type="button"
              className="modalBtn modalBtnSecondary"
              onClick={() => onClose(false)}
            >
              No
            </button>
          </footer>
        </div>
      </Modal>
    );
  }

  return (
    <Modal isOpen={true} onClose={() => onClose()}>
      <div className="modalBody">
        <div className="modalMessage">{message}</div>
        <footer className="modalFooter">
          <button
            ref={primaryButtonRef}
            type="button"
            className="modalBtn primary"
            onClick={() => onClose()}
          >
            OK
          </button>
        </footer>
      </div>
    </Modal>
  );
}

/**
 * Prompt modal component with a textarea input.
 * Supports editable mode (with OK/Cancel) and read-only mode (OK only).
 * @param {Object} props
 * @param {string} props.message - The prompt message.
 * @param {string} [props.initialValue=""] - Initial textarea value.
 * @param {Function} props.onClose - Callback with (value, confirmed) arguments.
 * @param {boolean} [props.hasCallback=false] - If true, shows editable textarea with Cancel button.
 * @returns {JSX.Element}
 */
function PromptModal(props) {
  const { message, initialValue = "", onClose, hasCallback = false } = props;
  const [value, setValue] = useState(initialValue);
  const textareaRef = useRef(null);
  const primaryButtonRef = useRef(null);

  useLayoutEffect(() => {
    if (textareaRef.current) {
      if (hasCallback) {
        textareaRef.current.focus();
        textareaRef.current.setSelectionRange(0, initialValue.length);
      }

      // Calculate rows based on actual line count + buffer
      const lines = initialValue.split("\n");
      const lineCount = lines.length;

      // Add 2-3 extra rows for editing comfort
      const extraRows = hasCallback ? 3 : 2;
      const calculatedRows = lineCount + extraRows;

      // Calculate max rows based on viewport height
      // Assume ~20px per row, and max 60% of viewport height for textarea
      const maxViewportRows = Math.floor((window.innerHeight * 0.6) / 20);

      // Set rows: minimum 5, maximum based on viewport, with our calculated value in between
      textareaRef.current.rows = Math.min(Math.max(calculatedRows, 5), maxViewportRows, 30);
    }
  }, [initialValue, hasCallback]);

  const handleOk = () => {
    onClose(value, true);
  };

  const handleCancel = () => {
    onClose(null, false);
  };

  if (hasCallback) {
    return (
      <Modal isOpen={true} onClose={handleCancel}>
        <div className="modalBody">
          <div className="modalMessage">{message}</div>
          <textarea
            ref={textareaRef}
            className="modalTextarea"
            value={value}
            onChange={(e) => setValue(e.target.value)}
          />
          <footer className="modalFooter">
            <button
              ref={primaryButtonRef}
              type="button"
              className="modalBtn primary"
              onClick={handleOk}
            >
              OK
            </button>
            <button type="button" className="modalBtn modalBtnSecondary" onClick={handleCancel}>
              Cancel
            </button>
          </footer>
        </div>
      </Modal>
    );
  }

  return (
    <Modal isOpen={true} onClose={handleOk}>
      <div className="modalBody">
        <div className="modalMessage">{message}</div>
        <textarea
          ref={textareaRef}
          className="modalTextarea modalTextarea--readonly"
          value={value}
          readOnly
        />
        <footer className="modalFooter">
          <button
            ref={primaryButtonRef}
            type="button"
            className="modalBtn primary"
            onClick={handleOk}
          >
            OK
          </button>
        </footer>
      </div>
    </Modal>
  );
}

/**
 * Singleton modal manager. Creates a React root for rendering modal components
 * into a dedicated DOM container.
 * @type {{container: HTMLDivElement|null, root: Object|null, init: Function, render: Function, unmount: Function}}
 */
const modalManager = {
  container: null,
  root: null,

  init() {
    if (!this.container) {
      this.container = document.createElement("div");
      this.container.id = "modal-root";
      document.body.appendChild(this.container);
      this.root = createRoot(this.container);
    }
  },

  render(component) {
    this.init();
    this.root.render(component);
  },

  unmount() {
    if (this.root) {
      this.root.render(null);
    }
  },
};

// Override window.alert, window.prompt, window.confirm
window.alert = (message) => {
  return new Promise((resolve) => {
    modalManager.render(
      <AlertModal
        message={message}
        type="alert"
        onClose={() => {
          modalManager.unmount();
          resolve();
        }}
      />,
    );
  });
};

window.confirm = (message) => {
  return new Promise((resolve, reject) => {
    modalManager.render(
      <AlertModal
        message={message}
        type="confirm"
        onClose={(confirmed) => {
          modalManager.unmount();
          if (confirmed) {
            resolve();
          } else {
            reject();
          }
        }}
      />,
    );
  });
};

window.prompt = (message, initialValue = "", callback = null) => {
  return new Promise((resolve) => {
    const hasCallback = typeof callback === "function";

    modalManager.render(
      <PromptModal
        message={message}
        initialValue={initialValue}
        hasCallback={hasCallback}
        onClose={(value, confirmed) => {
          modalManager.unmount();
          if (hasCallback) {
            if (confirmed) {
              callback(value);
            }
            resolve(confirmed ? value : null);
          } else {
            resolve(value);
          }
        }}
      />,
    );
  });
};

// component rendering code starts here
(async () => {
  const SAME_TAB_LINK_SPLIT = "|";
  const NEW_TAB_LINK_SPLIT = "|||";
  const HEADER_SPLIT = "#";
  const TITLE_SPLIT = "!";
  const CODE_BLOCK_SPLIT = "```";
  const HTML_BLOCK_SPLIT = "---";
  const NAV_BLOCK_SPLIT = ":::";
  const TAB_SPLIT = ">>>";
  const TAB_TITLE_SPLIT = "|";
  const FAV_ICON_SPLIT = "@";

  let cacheId = parseInt(Date.now());

  const DEFAULT_SCHEMA_TO_RENDER = `
    ! Navigation ${new Date().toLocaleString()}

    # Main Link Section
    google finance | finance.google.com
    www.cnbc.com

    # Secondary Section
    sample alert js | javascript://alert('hello')
    sample prompt editable js | javascript://prompt('this is an editable prompt','initial value', (a) => {console.log('test123',a)})
    sample prompt readonly js | javascript://prompt('this is a read only prompt','initial value')
    sample confirm js | javascript://confirm('are you sure?')

    # Data URL Section
    sample data url | data:text/html,%3C!doctype%20html%3E%0A%3Chtml%3E%0A%20%20%3Chead%3E%0A%20%20%20%20%3Cmeta%20charset%3D%22UTF-8%22%20%2F%3E%0A%20%20%20%20%3Ctitle%3ELoading...%3C%2Ftitle%3E%0A%20%20%20%20%3Clink%20rel%3D%22stylesheet%22%20href%3D%22https%3A%2F%2Fsynle.github.io%2Fnav-generator%2Findex.css%22%20%2F%3E%0A%20%20%3C%2Fhead%3E%0A%20%20%3Cbody%3E%0A%20%20%20%20%3Cscript%20type%3D'schema'%3E!%20Navigation%202%2F6%2F2026%2C%207%3A32%3A52%20AM%0A%0A%23%20Main%20Link%20Section%0Agoogle%20finance%20%7C%20finance.google.com%0A%0A%23%20Secondary%20Section%0Asample%20alert%20js%20%7C%20javascript%3A%2F%2Falert('hello')%0Asample%20prompt%20editable%20js%20%7C%20javascript%3A%2F%2Fprompt('this%20is%20an%20editable%20prompt'%2C'initial%20value'%2C%20(a)%20%3D%3E%20%7Bconsole.log('test123'%2Ca)%7D)%0Asample%20prompt%20readonly%20js%20%7C%20javascript%3A%2F%2Fprompt('this%20is%20a%20read%20only%20prompt'%2C'initial%20value')%0Asample%20confirm%20js%20%7C%20javascript%3A%2F%2Fconfirm('are%20you%20sure%3F')%0A%0A%23%20Data%20URL%20Section%0Asample%20data%20url%20%7C%20data%3Atext%2Fhtml%2C%253C!doctype%2520html%253E%250A%253Chtml%253E%250A%2520%2520%253Chead%253E%250A%2520%2520%2520%2520%253Cmeta%2520charset%253D%2522UTF-8%2522%2520%252F%253E%250A%2520%2520%2520%2520%253Ctitle%253ELoading...%253C%252Ftitle%253E%250A%2520%2520%2520%2520%253Clink%2520rel%253D%2522stylesheet%2522%2520href%253D%2522http%253A%252F%252F127.0.0.1%253A8080%252Findex.css%2522%2520%252F%253E%250A%2520%2520%253C%252Fhead%253E%250A%2520%2520%253Cbody%253E%250A%2520%2520%2520%2520%253Cscript%2520type%253D'schema'%253E!%2520Navigation%25202%252F6%252F2026%252C%25207%253A32%253A52%2520AM%250A%250A%2523%2520Main%2520Link%2520Section%250Agoogle%2520finance%2520%257C%2520finance.google.com%250A%250A%2523%2520Secondary%2520Section%250Asample%2520alert%2520js%2520%257C%2520javascript%253A%252F%252Falert('hello')%250Asample%2520prompt%2520editable%2520js%2520%257C%2520javascript%253A%252F%252Fprompt('this%2520is%2520an%2520editable%2520prompt'%252C''%252C%2520(a)%2520%253D%253E%2520%257Bconsole.log('test123'%252Ca)%257D)%250Asample%2520prompt%2520readonly%2520js%2520%257C%2520javascript%253A%252F%252Fprompt('this%2520is%2520a%2520read%2520only%2520prompt')%250Asample%2520confirm%2520js%2520%257C%2520javascript%253A%252F%252Fconfirm('are%2520you%2520sure%253F')%250A%250A%2523%2520Data%2520URL%2520Section%250Asample%2520data%2520url%2520%257C%2520data%253Atext%252Fhtml%252C%25253Chtml%25253E%25250A%252520%252520%25253Chead%25253E%25250A%252520%252520%252520%252520%25253Cmeta%252520charset%25253D'utf-8'%252520%25252F%25253E%25250A%252520%252520%252520%252520%25253Ctitle%25253ELoading...%25253C%25252Ftitle%25253E%25250A%252520%252520%252520%252520%25253Clink%252520rel%25253D%252522stylesheet%252522%252520href%25253D%252522http%25253A%25252F%25252Flocalhost%25253A8080%25252Findex.css%252522%252520%25252F%25253E%25250A%252520%252520%25253C%25252Fhead%25253E%25250A%252520%252520%25253Cload%252520%25252F%25253E%25250A%252520%252520%25253Cscript%252520type%25253D'schema'%25253E!%252520Data%252520Test%252520Navigation%25250A%25250A%252523%252520Main%252520Section%25250Agoogle%25257Cgoogle.com%25253C%25252Fscript%25253E%25250A%252520%252520%25253Cscript%252520src%25253D'https%25253A%25252F%25252Funpkg.com%25252F%252540babel%25252Fstandalone%25252Fbabel.min.js'%25253E%25253C%25252Fscript%25253E%25250A%252520%252520%25253Cscript%252520src%25253D'http%25253A%25252F%25252Flocalhost%25253A8080%25252Findex.jsx'%252520type%25253D'text%25252Fbabel'%252520data-presets%25253D'react'%252520data-type%25253D'module'%25253E%25253C%25252Fscript%25253E%25250A%25253C%25252Fhtml%25253E%250A%250A%2523%2520Notes%250A%2560%2560%2560%250ATODO%25201%250ATODO%25202%250A%2560%2560%2560%250A%250A%2523%2520Tabs%250A%253E%253E%253EtabName1%257CblockId1%253E%253E%253EtabName2%257CblockId2%250A%250A%2560%2560%2560blockId1%250Asample%2520blockId1%250A%2560%2560%2560%250A%250A---blockId2%250A%253Cu%253E%253Cb%253Esample%2520html%253C%252Fb%253E%253C%252Fu%253E%2520blockId2%250A---%253C%252Fscript%253E%250A%2520%2520%2520%2520%253Cscript%2520src%253D%2522http%253A%252F%252F127.0.0.1%253A8080%252Findex.js%2522%253E%253C%252Fscript%253E%250A%2520%2520%253C%252Fbody%253E%250A%253C%252Fhtml%253E%0A%0A%23%20Notes%0A%60%60%60%0ATODO%201%0ATODO%202%0A%60%60%60%0A%0A%23%20Tabs%0A%3E%3E%3EtabName1%7CblockId1%3E%3E%3EtabName2%7CblockId2%0A%0A%60%60%60blockId1%0Asample%20blockId1%0A%60%60%60%0A%0A---blockId2%0A%3Cu%3E%3Cb%3Esample%20html%3C%2Fb%3E%3C%2Fu%3E%20blockId2%0A---%3C%2Fscript%3E%0A%20%20%20%20%3Cscript%20src%3D%22https%3A%2F%2Fsynle.github.io%2Fnav-generator%2Findex.js%22%3E%3C%2Fscript%3E%0A%20%20%3C%2Fbody%3E%0A%3C%2Fhtml%3E

    # Notes
    \`\`\`
    TODO 1
    TODO 2
    \`\`\`

    # Nested Nav Block
    :::
    # Embedded Section
    github | github.com
    hacker news | news.ycombinator.com
    :::

    # Advanced Tabs (code + html + nested nav)
    >>>Code>>>HTML>>>Nested Nav

    \`\`\`Code
    // sample code block inside a tab
    const greet = (name) => "hello, " + name;
    console.log(greet("world"));
    \`\`\`

    ---HTML
    <h3>HTML tab</h3>
    <p>This tab renders raw HTML &mdash; <b>bold</b>, <i>italic</i>, <u>underline</u>.</p>
    ---

    :::Nested Nav
    # Embedded links inside a tab
    google | google.com
    github | github.com
    hacker news ||| news.ycombinator.com
    :::
  `
    .split("\n")
    .map((s) => s.trim())
    .join("\n");

  /**
   * Opens a data URL by extracting its schema and posting it to a new window.
   * Falls back to displaying the URL in a prompt if parsing fails.
   * Always posts the schema into a new child window.
   * @param {string} base64URL - The data URL to navigate to.
   * @returns {Promise<void>}
   */
  async function _navigateToDataUrl(base64URL) {
    try {
      const parser = new DOMParser();
      const doc = parser.parseFromString(
        decodeURIComponent(base64URL.replace("data:text/html,", "")),
        "text/html",
      );
      const schema = doc.querySelector("[type=schema]").innerText.trim();
      const childWindow = window.open(`${APP_INDEX_URL}?loadNav`);

      // post message to redirect the data url accordingly
      const intervalTryPostingMessage = setInterval(_doPostMessage, 100);
      setTimeout(() => clearInterval(intervalTryPostingMessage), 5000);

      function _doPostMessage() {
        childWindow.postMessage({ type: "onViewLinks", schema }, "*");
      }
    } catch {
      // show it in the prompt
      await prompt("Data URL (Copy to clipboard):", base64URL);
    }
  }

  /**
   * Generates a data URL bookmarklet from a navigation schema string.
   * @param {string} input - The raw schema string.
   * @returns {string} A data:text/html URL containing the schema as an embeddable page.
   */
  function _getNavBookmarkletFromSchema(input) {
    input = input.trim();

    let rawOutput = `
<!doctype html>
<html>
  <head>
    <meta charset="UTF-8" />
    <title>Loading...</title>
  </head>
  <body>
    <js_script type='schema'>${input}</js_script>
    <js_script src="${APP_UPSTREAM_DEFAULT_BASE_URL}/index.js"></js_script>
  </body>
</html>
    `
      .trim()
      .replace(/js_script/g, "script");

    return "data:text/html," + encodeURIComponent(rawOutput);
  }

  /**
   * Dispatches a legacy MouseEvent on a target element.
   * @param {EventTarget} target - The DOM element to dispatch the event on.
   * @param {string} evName - The event name (e.g. "click").
   * @param {Object} [evExtra={}] - Additional properties to attach to the event object.
   */
  function _dispatchEvent(target, evName, evExtra = {}) {
    const evType = "MouseEvents";
    const evObj = document.createEvent(evType);
    evObj.initEvent(evName, true, false);

    for (const extraKey of Object.keys(evExtra)) {
      evObj[extraKey] = evExtra[extraKey];
    }

    target.dispatchEvent(evObj);
  }

  /**
   * Dispatches a custom Event on a target element with extra properties.
   * @param {EventTarget} target - The DOM element to dispatch the event on.
   * @param {string} evName - The event name.
   * @param {Object} [evExtra={}] - Additional properties to attach to the event object.
   */
  function _dispatchCustomEvent(target, evName, evExtra = {}) {
    const evObj = new Event(evName);

    for (const extraKey of Object.keys(evExtra)) {
      evObj[extraKey] = evExtra[extraKey];
    }

    target.dispatchEvent(evObj);
  }

  // ---------------------------------------------------------------------------
  // Tab selection persistence (sessionStorage)
  // ---------------------------------------------------------------------------
  // Generated DOM ids look like `block_<cacheId>_<userId>` where `cacheId` is
  // seeded from `Date.now()` per parse, so the prefix is unstable across page
  // loads. The `<userId>` suffix (the id the schema author wrote) IS stable, so
  // that's what we persist. A `<tabs>` element's "scope" is the joined list of
  // its tab children's user ids — stable as long as the schema's tab ids
  // haven't changed.
  const TAB_SELECTION_STORAGE_KEY = "navTabSelection";

  /**
   * Strip the `block_<digits>_` prefix from a generated tab id to recover the
   * user-supplied portion. Returns "" when no user id was supplied (the parser
   * tags those as `block_<digits>_generated`), since "generated" is not a
   * stable persistence key.
   * @param {string} rawId
   * @returns {string}
   */
  function _getUserTabIdPart(rawId) {
    const m = String(rawId || "").match(/^block_\d+_(.+)$/);
    if (!m) return "";
    return m[1] === "generated" ? "" : m[1];
  }

  /**
   * Build a stable scope key for a `<tabs>` element from its direct tab
   * children's user ids. Returns "" if any child lacks a user-supplied id, in
   * which case persistence is skipped (we have no stable handle for that tab).
   * @param {Element} tabsEl
   * @returns {string}
   */
  function _getTabsScopeKey(tabsEl) {
    const childTabs = [...tabsEl.children].filter(
      (c) => c.tagName && c.tagName.toLowerCase() === "tab",
    );
    const parts = childTabs.map((t) => _getUserTabIdPart(t.dataset?.tabId));
    if (parts.length === 0 || parts.some((p) => !p)) return "";
    return parts.join("|");
  }

  /**
   * Read the per-tabs selection map from sessionStorage. Returns {} on any error.
   * @returns {Object<string, string>}
   */
  function _readTabSelectionStore() {
    try {
      return JSON.parse(sessionStorage.getItem(TAB_SELECTION_STORAGE_KEY) || "{}") || {};
    } catch {
      return {};
    }
  }

  /**
   * Persist the selected tab for a `<tabs>` scope. No-op when either the scope
   * or the tab lacks a stable user id.
   * @param {Element} tabsEl
   * @param {Element} tabEl
   */
  function _saveSelectedTab(tabsEl, tabEl) {
    const scope = _getTabsScopeKey(tabsEl);
    const userId = _getUserTabIdPart(tabEl.dataset?.tabId);
    if (!scope || !userId) return;
    const store = _readTabSelectionStore();
    if (store[scope] === userId) return;
    store[scope] = userId;
    try {
      sessionStorage.setItem(TAB_SELECTION_STORAGE_KEY, JSON.stringify(store));
    } catch {}
  }

  /**
   * Clear all persisted tab selections. Called when the schema is replaced
   * (Apply from edit, Restore from version history, Chrome bookmark import,
   * etc.) since the previously-saved scope keys may no longer match the new
   * schema's tabs — and even when they do, the user's expectation after a
   * save is to start fresh on the first tab of each tabs block.
   */
  function _clearTabSelectionStore() {
    try {
      sessionStorage.removeItem(TAB_SELECTION_STORAGE_KEY);
    } catch {}
  }

  /**
   * Look up the previously-selected tab inside a `<tabs>` element. Returns the
   * matching `<tab>` child or null if there's no saved selection or the saved
   * id is no longer present.
   * @param {Element} tabsEl
   * @returns {Element|null}
   */
  function _findRestoredTab(tabsEl) {
    const scope = _getTabsScopeKey(tabsEl);
    if (!scope) return null;
    const store = _readTabSelectionStore();
    const userId = store[scope];
    if (!userId) return null;
    const childTabs = [...tabsEl.children].filter(
      (c) => c.tagName && c.tagName.toLowerCase() === "tab",
    );
    return childTabs.find((t) => _getUserTabIdPart(t.dataset?.tabId) === userId) || null;
  }

  /**
   * Creates a data URL for downloading a schema as a plain text file.
   * @param {string} schema - The schema text to encode.
   * @returns {string} A data:text/plain URL.
   */
  function _getUrlDownloadSchema(schema) {
    return `data:text/plain,${encodeURIComponent(schema)}`;
  }

  /**
   * Safely stores a value in sessionStorage.
   * @param {string} key - The storage key.
   * @param {string} value - The value to store.
   */
  function _setSessionValue(key, value) {
    try {
      sessionStorage[key] = value;
    } catch {
      // Session storage not available
    }
  }

  /**
   * Safely retrieves a value from sessionStorage.
   * @param {string} key - The storage key.
   * @returns {string} The stored value, or empty string if unavailable.
   */
  function _getSessionValue(key) {
    try {
      return sessionStorage[key] || "";
    } catch {
      return "";
    }
  }

  /**
   * Safely stores a value in localStorage.
   * @param {string} key - The storage key.
   * @param {string} value - The value to store.
   */
  function _setLocalValue(key, value) {
    try {
      localStorage[key] = value;
    } catch {
      // Local storage not available
    }
  }

  /**
   * Safely retrieves a value from localStorage.
   * @param {string} key - The storage key.
   * @returns {string} The stored value, or empty string if unavailable.
   */
  function _getLocalValue(key) {
    try {
      return localStorage[key] || "";
    } catch {
      return "";
    }
  }

  /**
   * Persists the current schema buffer to sessionStorage.
   * @param {string} value - The schema string to persist.
   */
  function _persistBufferSchema(value) {
    _setSessionValue("schemaData", value);
  }

  /**
   * Retrieves the persisted schema buffer from sessionStorage.
   * @returns {string} The persisted schema string, or empty string.
   */
  function _getPersistedBufferSchema() {
    return _getSessionValue("schemaData");
  }

  const NAV_SCHEMA_CACHE_PREFIX = "navSchemaCache:";
  const NAV_LOG_PREFIX = "[nav-generator]";

  /**
   * Single prefixed logger for nav-generator lifecycle events — render
   * triggers ("render via …"), cache reads/writes/skips, NavBeforeLoad
   * dispatch. The shared `[nav-generator]` tag makes the full flow
   * filterable in devtools.
   * @param {string} message - Human-readable event description.
   * @param {Object} [details] - Optional structured detail object.
   */
  function _log(message, details) {
    if (details) {
      console.log(`${NAV_LOG_PREFIX} ${message}`, details);
    } else {
      console.log(`${NAV_LOG_PREFIX} ${message}`);
    }
  }

  /**
   * Computes the NavBeforeLoad cache key for the current page, or returns null
   * if caching shouldn't apply (non-http(s) URLs like data:, or editor/new-nav
   * flows where the rendered schema is user-supplied / in-flux).
   * @returns {string|null}
   */
  function _getNavSchemaCacheKey() {
    // Only cache real http(s) pages — skip data:, file:, etc.
    if (!/^https?:/i.test(location.href)) {
      _log("cache disabled: non-http(s) url", { href: location.href });
      return null;
    }
    // Skip nav-generator's own authoring / postMessage flows. These aren't
    // stable "read" renders; caching them would poison the next visit.
    if (/(?:^|[?&])newNav(?:[=&]|$)/.test(location.search)) {
      _log("cache disabled: ?newNav", { href: location.href });
      return null;
    }
    if (/(?:^|[?&])loadNav(?:[=&]|$)/.test(location.search)) {
      _log("cache disabled: ?loadNav", { href: location.href });
      return null;
    }
    return `${NAV_SCHEMA_CACHE_PREFIX}${location.href}`;
  }

  /**
   * Reads a previously cached schema for the current page, if any.
   * @param {string} key - Cache key from _getNavSchemaCacheKey.
   * @returns {string} Cached schema, or empty string.
   */
  function _readNavSchemaCache(key) {
    return _getLocalValue(key);
  }

  /**
   * Writes the latest schema for the current page to cache. No-ops when the
   * value is unchanged to avoid gratuitous writes.
   * @param {string} key - Cache key from _getNavSchemaCacheKey.
   * @param {string} value - The schema string to persist.
   */
  function _writeNavSchemaCache(key, value) {
    if (!value) return;
    if (_getLocalValue(key) === value) {
      _log("cache unchanged (skip write)", { key, length: value.length });
      return;
    }
    _setLocalValue(key, value);
    _log("cache updated", { key, length: value.length });
  }

  /**
   * Triggers a clipboard copy by dispatching a custom event.
   * @param {string} text - The text to copy to clipboard.
   */
  function _onCopyToClipboard(text) {
    _dispatchCustomEvent(document, "AppCopyTextToClipboard", { text });
  }

  /**
   * Dynamically loads an external script by appending a script tag.
   * @param {string} src - The script URL.
   * @returns {Promise<void>} Resolves when the script has loaded.
   */
  function _addScript(src) {
    return new Promise((resolve) => {
      const script = document.createElement("script");
      script.src = src;
      script.onload = resolve;
      document.head.appendChild(script);
    });
  }

  /**
   * Dynamically loads an external stylesheet by appending a link tag.
   * @param {string} src - The stylesheet URL.
   * @param {string} [rel="stylesheet/less"] - The link rel attribute.
   * @returns {Promise<void>} Resolves immediately after appending.
   */
  function _addStyle(src, rel = "stylesheet/less") {
    return new Promise((resolve) => {
      const link = document.createElement("link");
      link.rel = rel;
      link.href = src;
      document.head.appendChild(link);
      resolve();
    });
  }

  /**
   * Comparator for sorting schema sections alphabetically by their first element.
   * @param {string[]} a - First section array.
   * @param {string[]} b - Second section array.
   * @returns {number} Sort order: -1, 0, or 1.
   */
  function _schemaSectionNameOnlySorter(a, b) {
    const fa = a[0].toLowerCase();
    const fb = b[0].toLowerCase();

    if (fa > fb) {
      return 1;
    }

    if (fa < fb) {
      return -1;
    }

    return 0;
  }

  /**
   * Parses a raw schema string into a serialized array of schema components.
   * Handles titles, headers, links, code blocks, HTML blocks, nav blocks, tabs, and favicons.
   * Recognizes the exact TV directive only outside blocks and preserves TV link metadata.
   * @param {string} schema - The raw schema text.
   * @returns {Array<Object>} Array of serialized schema objects with type, key, value, etc.
   */
  function _getSerializedSchema(schema) {
    // parse lines and generate views
    const lines = schema
      .trim()
      .split("\n")
      .filter((r) => r.indexOf("//") !== 0)
      .map((r) => r.trimEnd());

    if (lines[0][0] !== "!") {
      const headerSchemaSampleCode = `${TITLE_SPLIT} Unnamed Navigation - ${new Date().toLocaleString()}`;
      lines.unshift(headerSchemaSampleCode);
    }

    let blockBuffer = [];
    let isInABlock = false;
    let blockType = ""; // code or html
    let currentHeaderName = "";
    let blockId = "";
    let pageFavIcon = "📑";

    let blockIdMap = {};
    let blockIdToTabNameMap = {};

    const serializedSchema = [];

    /**
     * Maps a raw block id to its unique cache-scoped id, generating one when absent.
     * @param {string} newBlockId - Raw block id from the schema line; empty string generates a fresh id.
     * @returns {string} Unique block id registered in blockIdMap.
     */
    const _upsertBlockId = (newBlockId) => {
      if (!newBlockId) {
        return `block_${++cacheId}_generated`;
      }

      if (!blockIdMap[newBlockId]) {
        blockIdMap[newBlockId] = `block_${++cacheId}_${newBlockId}`;
      }

      return blockIdMap[newBlockId];
    };

    lines.forEach((link) => {
      const newCacheId = ++cacheId;
      if (isInABlock) {
        let valueToUse = "";

        // attempt to format it as html
        valueToUse = blockBuffer.join("\n");
        try {
          valueToUse = JSON.stringify(JSON.parse(valueToUse), null, 2);
        } catch {}

        if (blockType === "code" && link.trim() === CODE_BLOCK_SPLIT) {
          const resolvedBlockId = _upsertBlockId(blockId);
          serializedSchema.push({
            key: newCacheId,
            id: resolvedBlockId,
            tabName: blockIdToTabNameMap[resolvedBlockId],
            value: valueToUse,
            type: "code_block",
          });
          isInABlock = false;
          blockBuffer = [];
          blockType = "";
          currentHeaderName = "";
          blockId = "";
        } else if (blockType === "html" && link.trim() === HTML_BLOCK_SPLIT) {
          serializedSchema.push({
            key: newCacheId,
            id: _upsertBlockId(blockId),
            value: valueToUse,
            type: "html_block",
          });
          isInABlock = false;
          blockBuffer = [];
          blockType = "";
          currentHeaderName = "";
          blockId = "";
        } else if (blockType === "nav" && link.trim() === NAV_BLOCK_SPLIT) {
          const resolvedBlockId = _upsertBlockId(blockId);
          serializedSchema.push({
            key: newCacheId,
            id: resolvedBlockId,
            tabName: blockIdToTabNameMap[resolvedBlockId],
            value: blockBuffer.join("\n"),
            type: "nav_block",
          });
          isInABlock = false;
          blockBuffer = [];
          blockType = "";
          currentHeaderName = "";
          blockId = "";
        } else {
          blockBuffer.push(link);
        }
        return;
      }

      if (link.trim() === TV_SCHEMA_MARKER) {
        serializedSchema.push({ key: newCacheId, type: "tv_mode" });
      } else if (link.trim().indexOf(FAV_ICON_SPLIT) === 0) {
        pageFavIcon = link.replace(/^[@]+/, "").trim();
        serializedSchema.push({
          key: newCacheId,
          value: pageFavIcon,
          type: "favIcon",
        });
      } else if (link.trim().indexOf(TITLE_SPLIT) === 0) {
        const headerText = link.replace(TITLE_SPLIT, "").trim();
        serializedSchema.push({
          key: newCacheId,
          value: headerText,
          type: "title",
        });
      } else if (link.trim().indexOf(HEADER_SPLIT) === 0) {
        const headerText = link.replace(HEADER_SPLIT, "").trim();
        serializedSchema.push({
          key: newCacheId,
          value: headerText,
          type: "header",
        });

        currentHeaderName = headerText;
      } else if (link.trim().indexOf(CODE_BLOCK_SPLIT) === 0) {
        isInABlock = true;
        blockType = "code";
        if (link.length > CODE_BLOCK_SPLIT.length) {
          blockId = link.substr(blockId.indexOf(CODE_BLOCK_SPLIT) + CODE_BLOCK_SPLIT.length + 1);
          _upsertBlockId(blockId);
        }
      } else if (link.trim().indexOf(HTML_BLOCK_SPLIT) === 0) {
        isInABlock = true;
        blockType = "html";
        if (link.length > HTML_BLOCK_SPLIT.length) {
          blockId = link.substr(blockId.indexOf(HTML_BLOCK_SPLIT) + HTML_BLOCK_SPLIT.length + 1);
          _upsertBlockId(blockId);
        }
      } else if (link.trim().indexOf(NAV_BLOCK_SPLIT) === 0) {
        isInABlock = true;
        blockType = "nav";
        if (link.length > NAV_BLOCK_SPLIT.length) {
          blockId = link.substr(blockId.indexOf(NAV_BLOCK_SPLIT) + NAV_BLOCK_SPLIT.length + 1);
          _upsertBlockId(blockId);
        }
      } else if (link.trim().indexOf(TAB_SPLIT) === 0) {
        let tabContent = [];
        link
          .split(TAB_SPLIT)
          .map((r) => r.trim())
          .filter((r) => !!r)
          .forEach((t) => {
            const [rawName, rawId] = t.split(TAB_TITLE_SPLIT);
            const tabName = (rawName || "").trim();
            // Short-form (`>>>Label`) falls back to using the label itself as
            // the binding key, so `:::Label` / ` ```Label ` content blocks
            // match without an explicit id. Long-form (`>>>Label|tabId`)
            // still works for backward compatibility with older schemas.
            const tabId = (rawId || "").trim() || tabName;
            if (tabName && tabId) {
              const resolvedTabId = _upsertBlockId(tabId);
              blockIdToTabNameMap[resolvedTabId] = tabName;
              tabContent.push({
                tabId: resolvedTabId,
                tabName,
              });
            }
          });

        serializedSchema.push({
          key: newCacheId,
          type: "tabs",
          tabContent: tabContent,
        });
      } else if (link.trim().length > 0) {
        let linkType;
        let linkText, linkUrl;
        const tvLink = parseTvLinkLine(link);
        if (tvLink) {
          if (!tvLink.linkUrl) return;
          ({ linkText, linkUrl } = tvLink);
          linkType = "sameTabLink";
        }

        try {
          // try parse as new tab link
          if (
            !linkType &&
            link.indexOf(NEW_TAB_LINK_SPLIT) !== -1 &&
            link.indexOf(NEW_TAB_LINK_SPLIT) <= link.indexOf(SAME_TAB_LINK_SPLIT)
          ) {
            linkText = link.substr(0, link.indexOf(NEW_TAB_LINK_SPLIT)).trim();
            linkUrl = link
              .substr(link.indexOf(NEW_TAB_LINK_SPLIT) + NEW_TAB_LINK_SPLIT.length)
              .trim();
            linkType = "newTabLink";
          }
        } catch {}

        if (!linkType) {
          try {
            if (link.length > 0 && SAME_TAB_LINK_SPLIT.includes(SAME_TAB_LINK_SPLIT)) {
              linkText = link.substr(0, link.indexOf(SAME_TAB_LINK_SPLIT)).trim();
              linkUrl = link
                .substr(link.indexOf(SAME_TAB_LINK_SPLIT) + SAME_TAB_LINK_SPLIT.length)
                .trim();
              linkType = "sameTabLink";
            }
          } catch {}
        }
        if (linkType) {
          if (linkUrl.indexOf("/") === 0) {
            linkUrl = `${location.origin}${linkUrl}`;
          } else if (
            linkUrl.indexOf("http://") !== 0 &&
            linkUrl.indexOf("https://") !== 0 &&
            linkUrl.indexOf("javascript://") !== 0 &&
            linkUrl.indexOf("data:") !== 0
          ) {
            linkUrl = `https://${linkUrl}`;
          }

          if (linkUrl.indexOf("javascript://") === 0) {
            linkType = "jsLink";
            linkUrl = linkUrl.replace("javascript://", "");
            linkUrl = `(async() => {${linkUrl}})()`;
          } else if (linkUrl.indexOf("data:") === 0) {
            linkType = "dataLink";
          }

          if (!linkText) {
            // Generate linkText from domain if not provided
            try {
              if (linkType === "jsLink") {
                linkText = "JS Link";
              } else if (linkType === "dataLink") {
                linkText = "Data URL Link";
              } else {
                // Generate linkText from domain for regular URLs only
                const url = new URL(linkUrl);
                let hostname = url.hostname.replace(/^www\./, "");
                const parts = hostname.split(".");
                // Get root domain name (second-to-last part before TLD)
                linkText = parts.length >= 2 ? parts[parts.length - 2] : hostname;
              }
            } catch {
              // If URL parsing fails, use the raw linkUrl
              linkText = linkUrl.substr(0, 20) + "...";
            }
          }

          serializedSchema.push({
            key: newCacheId,
            type: "link",
            linkUrl,
            linkText,
            linkType,
            isTvLink: !!tvLink,
            headerName: currentHeaderName,
          });
        }
      }
    });

    return serializedSchema;
  }

  /**
   * Search input component with fuzzy search support and help popup.
   * @param {Object} props
   * @param {Function} props.onSearch - Callback with search text on input change.
   * @param {string} props.searchText - Current search text value.
   * @param {Function} props.onClear - Callback to clear the search.
   * @returns {JSX.Element}
   */
  function SearchBox(props) {
    const { onSearch, searchText, onClear } = props;
    const [showHelp, setShowHelp] = useState(false);

    return (
      <div className="search-container">
        <div className="search-input-wrapper">
          <span className="search-icon">🔍</span>
          <input
            id="search"
            onInput={(e) => onSearch(e.target.value)}
            placeholder="Search bookmarks..."
            autoComplete="off"
            spellCheck="false"
            value={searchText}
          />
          {searchText && (
            <button
              type="button"
              className="search-clear-btn"
              onClick={onClear}
              aria-label="Clear search"
              title="Clear search"
            >
              ✕
            </button>
          )}
          <button
            type="button"
            className="search-help-btn"
            onClick={() => setShowHelp(!showHelp)}
            aria-label="Search help"
            title="Search shortcuts"
          >
            ?
          </button>
        </div>
        {showHelp && (
          <div className="search-help-popup">
            <div className="search-help-header">
              Search Shortcuts
              <button
                type="button"
                className="search-help-close"
                onClick={() => setShowHelp(false)}
              >
                ✕
              </button>
            </div>
            <div className="search-help-content">
              <div className="search-help-item">
                <code>/term</code>
                <span>Fuzzy search (matches scattered letters)</span>
              </div>
              <div className="search-help-item">
                <code>?term</code>
                <span>Google search on submit</span>
              </div>
              <div className="search-help-item">
                <code>Enter</code>
                <span>Navigate if single result</span>
              </div>
              <div className="search-help-item">
                <code>Esc</code>
                <span>Clear search and blur</span>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  /**
   * Read-only view page component. Renders the navigation schema with search,
   * keyboard navigation, and search highlighting. Arrow navigation skips hidden and disabled links.
   * TV mode hides and suspends search, and arrows recover focus at the first eligible link.
   * @param {Object} props
   * @param {string} props.schema - The navigation schema string.
   * @param {Function} props.onSetViewMode - Callback to switch view modes.
   * @param {Function} props.onSetSchema - Callback to update the schema.
   * @returns {JSX.Element}
   */
  function PageRead(props) {
    const { schema, onSetViewMode } = props;
    const [searchText, setSearchText] = useState("");
    const [resultCount, setResultCount] = useState(0);
    const tvMode = useTvMode();

    const refContainer = useRef();

    // events
    const onSearch = useCallback((newSearchText) => {
      setSearchText(newSearchText);
    }, []);

    const onClearSearch = useCallback(() => {
      setSearchText("");
      setResultCount(0);
      document.querySelector("#search")?.focus();
    }, []);

    const onSubmitNavigationSearch = useCallback(
      (e) => {
        e.preventDefault();

        if (refContainer && refContainer.current) {
          const doc = refContainer.current;

          const [firstSearchChar, ...searchWord] = searchText;
          if (firstSearchChar === "?") {
            location.href = `https://www.google.com/search?q=${searchWord.join("")}`;
          }

          const links = getFocusableNavLinks(doc);

          // focus on the first link
          if (links.length > 0) {
            links[0].focus();
          }

          // only 1 result, then let's redirect
          if (links.length === 1 && links[0].href) {
            location.href = links[0].href;
          }
        }

        return false;
      },
      [searchText],
    );

    // handling search
    useLayoutEffect(() => {
      const trimmedSearchText = tvMode ? "" : (searchText || "").trim();

      if (!refContainer?.current) return;

      const doc = refContainer.current;
      let linkSelector = ".link:not([data-tv-link])";
      if (tvMode) {
        linkSelector = doc.hasAttribute("data-tv-schema")
          ? ":scope > .link[data-tv-link]"
          : ".link";
      }
      const links = doc.querySelectorAll(linkSelector);
      const otherNonLinks = doc.querySelectorAll(".schema-render-wrapper > *");
      const allElems = [...links, ...otherNonLinks];

      // Helper to clear all search highlights from links
      function clearHighlights() {
        doc.querySelectorAll(".link mark.search-highlight").forEach((mark) => {
          const parent = mark.parentNode;
          parent.replaceChild(document.createTextNode(mark.textContent), mark);
          parent.normalize();
        });
      }

      // Reset case
      if (!trimmedSearchText) {
        setResultCount(links.length);
        allElems.forEach((elem) => elem.classList.toggle("hidden", false));
        clearHighlights();
        return;
      } else {
        allElems.forEach((elem) => elem.classList.toggle("hidden", true));
      }

      function escapeRegex(str) {
        return str.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
      }

      const isFuzzy = trimmedSearchText.startsWith("/");

      // Match logic:
      //  - Fuzzy (/term): single regex, scattered-letter match.
      //  - Non-fuzzy: whitespace-tokenized, AND across tokens. e.g. "commit fe"
      //    matches "commits fe" because both "commit" and "fe" appear — the
      //    user shouldn't have to type a contiguous substring.
      let matchRegexes; // array of RegExp; ALL must match (text or section)
      let highlightRegex; // single RegExp used to highlight matched portions

      if (isFuzzy) {
        const cleaned = trimmedSearchText
          .slice(1)
          .replace(/[\W_]+/g, " ")
          .replace(/\s+/g, " ")
          .trim();

        const matchPattern = cleaned
          .split("")
          .map((c) => escapeRegex(c))
          .join(".*?");
        matchRegexes = [new RegExp(matchPattern, "i")];

        const highlightPattern = cleaned
          .split("")
          .map((c) => "(" + escapeRegex(c) + ")")
          .join("(.*?)");
        highlightRegex = new RegExp(highlightPattern, "i");
      } else {
        const tokens = trimmedSearchText.split(/\s+/).filter(Boolean);
        matchRegexes = tokens.map((tok) => new RegExp(escapeRegex(tok), "i"));
        // Alternation regex highlights any token's occurrences.
        highlightRegex = new RegExp("(" + tokens.map(escapeRegex).join("|") + ")", "gi");
      }

      // Apply highlight markup to a text node
      function applyHighlightToTextNode(textNode, regex, fuzzy) {
        const text = textNode.textContent;
        const match = regex.exec(text);
        if (!match) return;

        const frag = document.createDocumentFragment();

        if (fuzzy) {
          // For fuzzy matches, each odd-indexed capture group is a matched char
          const fullMatchStart = match.index;
          const fullMatchEnd = match.index + match[0].length;

          // Text before the full match
          if (fullMatchStart > 0) {
            frag.appendChild(document.createTextNode(text.substring(0, fullMatchStart)));
          }

          // Walk through matched portion character by character
          let innerPos = fullMatchStart;
          for (let g = 1; g < match.length; g++) {
            if (match[g] === undefined) continue;
            // For odd groups (the actual matched chars), highlight them
            if (g % 2 === 1) {
              const mark = document.createElement("mark");
              mark.className = "search-highlight";
              mark.textContent = match[g];
              frag.appendChild(mark);
            } else {
              // Even groups are the in-between text
              frag.appendChild(document.createTextNode(match[g]));
            }
            innerPos += match[g].length;
          }

          // Text after the full match
          if (fullMatchEnd < text.length) {
            frag.appendChild(document.createTextNode(text.substring(fullMatchEnd)));
          }
        } else {
          // Simple substring highlight - highlight all occurrences of any token.
          // The supplied regex is an alternation of all tokens with /gi.
          let lastIndex = 0;
          const globalRegex = new RegExp(regex.source, "gi");
          let m;
          while ((m = globalRegex.exec(text)) !== null) {
            if (m.index > lastIndex) {
              frag.appendChild(document.createTextNode(text.substring(lastIndex, m.index)));
            }
            const mark = document.createElement("mark");
            mark.className = "search-highlight";
            mark.textContent = m[1];
            frag.appendChild(mark);
            lastIndex = m.index + m[0].length;
          }
          if (lastIndex < text.length) {
            frag.appendChild(document.createTextNode(text.substring(lastIndex)));
          }
          if (lastIndex === 0) return; // no matches found
        }

        textNode.parentNode.replaceChild(frag, textNode);
      }

      // Highlight matching text inside a link element
      function highlightLink(elem) {
        // Walk text nodes and apply highlights
        const walker = document.createTreeWalker(elem, NodeFilter.SHOW_TEXT, null);
        const textNodes = [];
        let node;
        while ((node = walker.nextNode())) {
          textNodes.push(node);
        }
        textNodes.forEach((tn) => {
          applyHighlightToTextNode(tn, isFuzzy ? highlightRegex : highlightRegex, isFuzzy);
          // Reset lastIndex for regex reuse
          highlightRegex.lastIndex = 0;
        });
      }

      let visibleCount = 0;

      // Clear previous highlights before applying new ones
      clearHighlights();

      // Map nav-block / code-block id -> tab label(s) that reference it. Lets a
      // search for the tab name (e.g. "tde") match links inside the tab-bound
      // block, even though the links themselves don't carry the tab text.
      const blockIdToTabLabels = new Map();
      doc.querySelectorAll("tab[data-tab-id]").forEach((tab) => {
        const id = tab.dataset.tabId;
        if (!id) return;
        const label = (tab.textContent || "").trim();
        if (!label) return;
        if (!blockIdToTabLabels.has(id)) blockIdToTabLabels.set(id, []);
        blockIdToTabLabels.get(id).push(label);
      });

      /**
       * Collect ancestor context for matching: any tab labels whose content
       * blocks contain this element. Walks up to the search container.
       */
      function getAncestorContext(elem) {
        const labels = [];
        let parent = elem.parentElement;
        while (parent && parent !== doc) {
          if (parent.id && blockIdToTabLabels.has(parent.id)) {
            labels.push(...blockIdToTabLabels.get(parent.id));
          }
          parent = parent.parentElement;
        }
        return labels.join(" ");
      }

      // Only consider links for matching. ALL match regexes must pass against
      // the link text, its section header, or any ancestor tab label (so
      // searching "tde" surfaces every link inside the :::tde nav-block, and
      // "deploy commits" matches a "commits fe" link in the "Deploy" section
      // even though the tokens straddle multiple fields).
      links.forEach((elem) => {
        const text = elem.innerText || "";
        const section = elem.dataset.section || "";
        const context = getAncestorContext(elem);

        const isMatch = matchRegexes.every(
          (r) => r.test(text) || r.test(section) || r.test(context),
        );

        elem.classList.toggle("hidden", !isMatch);

        if (isMatch) {
          visibleCount++;
          highlightLink(elem);
          // The mass-hide above hides every direct child of the search
          // container — including ancestor wrappers like .block.navBlock
          // that contain the matched link. Walk up and un-hide ancestors
          // so the matched link is actually rendered. Stop at the
          // container so the wrapper itself isn't touched.
          let parent = elem.parentElement;
          while (parent && parent !== doc) {
            parent.classList.remove("hidden");
            parent = parent.parentElement;
          }
        }
      });

      setResultCount(visibleCount);

      // Scroll to top to show search results
      window.scrollTo(0, 0);
    }, [searchText, tvMode]);

    // Arrow key navigation for links in the grid
    useEffect(() => {
      const container = refContainer.current;
      if (!container) return;

      /**
       * Moves between visible links or starts TV navigation without taking over editing or dialogs.
       * @param {KeyboardEvent} e - Keyboard event from the navigation root or TV document.
       * @returns {void} Prevents scrolling only when focusing an eligible link.
       */
      function handleKeyDown(e) {
        if (e.defaultPrevented) return;
        if (!["ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight"].includes(e.key)) return;

        const focused = document.activeElement;
        if (
          tvMode &&
          (focused?.closest(
            "input, textarea, select, [contenteditable]:not([contenteditable='false'])",
          ) ||
            document.querySelector(".modal, [role='dialog']"))
        )
          return;

        const allLinks = getFocusableNavLinks(container);
        const currentIndex = allLinks.indexOf(focused);
        if (currentIndex === -1) {
          if (tvMode && allLinks.length > 0) {
            e.preventDefault();
            allLinks[0].focus();
          }
          return;
        }

        let targetIndex = -1;

        if (e.key === "ArrowLeft") {
          targetIndex = currentIndex - 1;
        } else if (e.key === "ArrowRight") {
          targetIndex = currentIndex + 1;
        } else if (e.key === "ArrowUp" || e.key === "ArrowDown") {
          // Find the link above/below by comparing visual positions
          const focusedRect = focused.getBoundingClientRect();
          const focusCenterX = focusedRect.left + focusedRect.width / 2;
          let bestMatch = null;
          let bestDistance = Infinity;

          for (let i = 0; i < allLinks.length; i++) {
            if (i === currentIndex) continue;
            const rect = allLinks[i].getBoundingClientRect();
            const centerY = rect.top + rect.height / 2;
            const focusCenterY = focusedRect.top + focusedRect.height / 2;

            const isCorrectDirection =
              e.key === "ArrowDown" ? centerY > focusCenterY + 1 : centerY < focusCenterY - 1;
            if (!isCorrectDirection) continue;

            const verticalDist = Math.abs(centerY - focusCenterY);
            const horizontalDist = Math.abs(rect.left + rect.width / 2 - focusCenterX);
            // Prioritize vertical proximity, then horizontal closeness
            const distance = verticalDist * 1000 + horizontalDist;

            if (distance < bestDistance) {
              bestDistance = distance;
              bestMatch = i;
            }
          }

          if (bestMatch !== null) targetIndex = bestMatch;
        }

        if (targetIndex >= 0 && targetIndex < allLinks.length) {
          e.preventDefault();
          allLinks[targetIndex].focus();
        }
      }

      const keyboardRoot = tvMode ? document : container;
      keyboardRoot.addEventListener("keydown", handleKeyDown);
      return () => keyboardRoot.removeEventListener("keydown", handleKeyDown);
    }, [tvMode]);

    // Force full reload when user navigates back (bfcache)
    useEffect(() => {
      function onPageShow(e) {
        if (e.persisted) {
          window.location.reload();
        }
      }

      window.addEventListener("pageshow", onPageShow);
      return () => window.removeEventListener("pageshow", onPageShow);
    }, []);

    return (
      <>
        {!tvMode && searchText && (
          <div className="search-result-count">
            {resultCount === 0 ? (
              <>
                No matches found.{" "}
                <a
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    onClearSearch();
                  }}
                >
                  Click here to reset
                </a>
                .
              </>
            ) : (
              <>
                {resultCount} matches found.{" "}
                <a
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    onClearSearch();
                  }}
                >
                  Click here to reset search
                </a>
                .
              </>
            )}
          </div>
        )}
        <div id="fav" ref={refContainer} className="schema-render-wrapper">
          <SchemaRender schema={schema} refContainer={refContainer} onSetViewMode={onSetViewMode} />
        </div>

        {!tvMode && (
          <form
            id="searchForm"
            className="form-search-excluded"
            onSubmit={(e) => onSubmitNavigationSearch(e)}
          >
            <SearchBox onSearch={onSearch} searchText={searchText} onClear={onClearSearch} />
          </form>
        )}
      </>
    );
  }

  /**
   * Edit page component with Monaco editor for modifying the navigation schema.
   * Supports apply, cancel, sort, test, and bookmark generation.
   * @param {Object} props
   * @param {string} props.schema - The current navigation schema.
   * @param {Function} props.onSetViewMode - Callback to switch view modes.
   * @param {Function} props.onSetSchema - Callback to update the schema.
   * @returns {JSX.Element}
   */
  function PageEdit(props) {
    const { schema, onSetViewMode, onSetSchema } = props;
    const [bufferSchema, setBufferSchema] = useState(schema.trim());
    const [hasPendingChanges, setHasPendingChanges] = useState(false);
    const [bookmark, setBookmark] = useState("");

    // events
    const onApply = useCallback(() => {
      onSetSchema(bufferSchema); // update schema
      onSetViewMode("read");

      //update the cache in the session storage
      _persistBufferSchema(bufferSchema); // commit the changes
      createVersion(bufferSchema); // persist the schema in indexed db for version history
    }, [bufferSchema]);

    const onCancel = useCallback(async () => {
      if (hasPendingChanges) {
        try {
          await confirm("You have unsaved changes. Discard unsaved changes?");
        } catch {
          // user cancel, then stop...
          return;
        }
      }

      onSetViewMode("read");
    }, []);

    const onTest = useCallback(() => {
      const base64URL = _getNavBookmarkletFromSchema(bufferSchema);
      _navigateToDataUrl(base64URL);
    }, [bufferSchema]);

    const onSetBufferSchema = useCallback((newBufferSchema) => {
      setBufferSchema(newBufferSchema);
      setHasPendingChanges(true);
    }, []);

    /**
     * Sorts schema sections alphabetically by section header name.
     * @param {string} schemaText - The schema string to sort.
     */
    function onSortSchemaBySectionNameAndTitle(schemaText) {
      const rows = schemaText.split("\n");
      let sections = [];
      let sectionIdx = 0;

      for (const row of rows) {
        if (row[0] === "#") {
          sectionIdx++;
        }
        sections[sectionIdx] = sections[sectionIdx] || [];
        sections[sectionIdx].push(row);
      }

      sections = sections
        .filter((s) => !!s && s.length > 0)
        .map((s) => {
          if (s[s.length - 1] !== "") {
            s.push("");
          }
          return s;
        })
        .toSorted(_schemaSectionNameOnlySorter);

      const newBufferSchema = sections.map((s) => s.join("\n")).join("\n");
      setBufferSchema(newBufferSchema);
    }

    // effects
    useLayoutEffect(() => {
      // store it into cache
      _persistBufferSchema(schema);

      // hook up the tab and shift tab to do modification
      return () => {
        window.onbeforeunload = undefined;
      };
    }, []);

    // trigger the confirmation to save before unload
    useLayoutEffect(() => {
      if (hasPendingChanges) {
        window.onbeforeunload = function (e) {
          e.preventDefault();
          return (e.returnValue = "You have unsaved changes. Do you want to continue with exit?");
        };
      }
    }, [hasPendingChanges]);

    // update bookmarklet
    useLayoutEffect(() => {
      setBookmark(_getNavBookmarkletFromSchema(bufferSchema));
    }, [bufferSchema]);

    // generate the view
    return (
      <div id="command">
        <div className="title">
          Edit Navigation
          <div className="action-bar">
            <button id="applyEdit" type="button" role="button" onClick={() => onApply()}>
              Apply
            </button>
            <button id="cancelEdit" type="button" role="button" onClick={() => onCancel()}>
              Cancel
            </button>
            <DropdownButtons>
              <button className="dropdown-trigger" type="button">
                Actions
              </button>
              <a role="button" target="_blank" href={NEW_NAV_URL}>
                New Nav
              </a>
              <button onClick={() => onSortSchemaBySectionNameAndTitle(bufferSchema)}>
                Sort Schema
              </button>
              <button
                className="copyBookmarkToClipboard"
                onClick={() => _onCopyToClipboard(bookmark)}
              >
                Copy Bookmark
              </button>
              <button onClick={() => _onCopyToClipboard(bufferSchema)}>Copy Schema</button>
              <a
                role="button"
                target="_blank"
                href="https://github.com/synle/nav-generator/blob/main/index.jsx"
              >
                JS Code
              </a>
              <a
                role="button"
                target="_blank"
                href="https://github.com/synle/nav-generator/blob/main/index.scss"
              >
                CSS Code
              </a>
              <button type="button" onClick={onTest}>
                Test
              </button>
              <a
                role="button"
                href={_getUrlDownloadSchema(schema)}
                download={`schema.${new Date().getTime()}.txt`}
              >
                Download Schema
              </a>
              <a role="button" href={bookmark} download={`bookmark.${new Date().getTime()}.html`}>
                Download Bookmark
              </a>
            </DropdownButtons>
          </div>
        </div>
        <SchemaEditor
          id="input"
          wrap="soft"
          spellcheck="false"
          value={bufferSchema}
          onInput={(e) => onSetBufferSchema(e.target.value)}
          onBlur={(e) => onSetBufferSchema(e.target.value)}
        ></SchemaEditor>
      </div>
    );
  }

  /**
   * Gets a favicon URL for a given website URL using Google's favicon service.
   * @param {string} url - The website URL to get the favicon for.
   * @returns {string} The Google favicon service URL.
   */
  function _getFaviconUrl(url) {
    // Extract domain from URL
    let domain = url.match(/^(?:https?:\/\/)?(?:[^@\n]+@)?(?:www\.)?([^:/\n]+)/im)[1];

    // Use Google's favicon service to avoid direct local network access
    return `https://www.google.com/s2/favicons?domain=${domain}&sz=32`;
  }

  /**
   * Favicon component that displays a website's favicon image or a fallback emoji.
   * @param {Object} props
   * @param {string} props.linkUrl - The URL to fetch the favicon for.
   * @param {string} props.linkType - The link type (e.g. "sameTabLink", "jsLink").
   * @returns {JSX.Element}
   */
  function FavIcon(props) {
    const { linkUrl, linkType } = props;
    const [error, setError] = useState(false);

    if (!error) {
      switch (linkType) {
        default:
          if (linkUrl.indexOf("http://") === 0 || linkUrl.indexOf("https://") === 0) {
            let favIconUrl = _getFaviconUrl(linkUrl);
            return (
              <img
                src={favIconUrl}
                alt="Fav"
                onError={() => {
                  setError(true);
                }}
              />
            );
          }
          break;
      }
    }

    return <span>📙</span>;
  }

  // Broadcast bus for the global "collapse/expand all code blocks" shortcut.
  // Alt+\ / Cmd+\ flips _codeBlocksAllCollapsed and dispatches the event;
  // every mounted CodeBlockWrapper listens and snaps to the new state.
  // Newly-mounted wrappers inherit the current flag so a re-render after
  // the shortcut was pressed still starts collapsed.
  const CODE_BLOCK_COLLAPSE_EVENT = "NavGenCodeBlockCollapseAll";
  let _codeBlocksAllCollapsed = false;

  /** Number of leading content lines shown when a code block is collapsed. */
  const COLLAPSED_PREVIEW_LINES = 10;

  /**
   * Reusable collapsible code block wrapper with copy, fullscreen, and collapse toggle.
   * @param {Object} props
   * @param {string} props.id - DOM element ID.
   * @param {string} props.title - Title displayed in the banner.
   * @param {string} [props.content=""] - Raw text content for copy/fullscreen.
   * @param {React.ReactNode} [props.extraButtons] - Additional action buttons.
   * @param {boolean} [props.defaultCollapsed=true] - Whether to start collapsed.
   * @param {React.ReactNode} props.children - The code block content to render.
   * @returns {JSX.Element}
   */
  function CodeBlockWrapper(props) {
    const { id, title, content = "", extraButtons, defaultCollapsed = true, children } = props;
    const [collapsed, setCollapsed] = useState(defaultCollapsed || _codeBlocksAllCollapsed);
    const [fullscreen, setFullscreen] = useState(false);

    // Subscribe to the global collapse/expand broadcast so Alt+\ / Cmd+\
    // flips every visible code block in lockstep.
    useEffect(() => {
      const handler = (e) => setCollapsed(!!e.detail?.collapsed);
      document.addEventListener(CODE_BLOCK_COLLAPSE_EVENT, handler);
      return () => document.removeEventListener(CODE_BLOCK_COLLAPSE_EVENT, handler);
    }, []);

    useLayoutEffect(() => {
      if (!fullscreen) return;
      const handleEscape = (e) => {
        if (e.key === "Escape") setFullscreen(false);
      };
      document.addEventListener("keydown", handleEscape);
      return () => document.removeEventListener("keydown", handleEscape);
    }, [fullscreen]);

    const codeBlockLang = _detectCodeLanguage(content);

    // When collapsed, render only the first COLLAPSED_PREVIEW_LINES of `content`
    // so users can glance at the start of the block. The "Show More" link below
    // expands to the full caller-provided children. Re-uses _highlightCode so the
    // truncated preview keeps Prism syntax highlighting.
    const contentLines = content.split("\n");
    const isTruncatable = contentLines.length > COLLAPSED_PREVIEW_LINES;
    const hiddenLineCount = isTruncatable ? contentLines.length - COLLAPSED_PREVIEW_LINES : 0;

    const titleDom = title ? (
      <span
        className="codeBlockTitle"
        onClick={() => _onCopyToClipboard(content)}
        style={{ cursor: "pointer" }}
      >
        {title}
      </span>
    ) : null;

    // No per-block collapse toggle: the inline "Show More" link below handles
    // expansion when the block is collapsed, and the global Alt+\ / Cmd+\
    // shortcut handles bulk collapse/expand.
    const actionsDom = (
      <div className="codeBlockActions">
        {extraButtons}
        <button onClick={() => _onCopyToClipboard(content)}>Copy</button>
        <button onClick={() => setFullscreen(true)}>Fullscreen</button>
      </div>
    );

    let bodyDom = null;
    if (collapsed && isTruncatable) {
      const previewText = contentLines.slice(0, COLLAPSED_PREVIEW_LINES).join("\n");
      bodyDom = (
        <>
          <div className="codeBlockContent">
            <pre
              className={`block codeBlock language-${codeBlockLang}`}
              dangerouslySetInnerHTML={{ __html: _highlightCode(previewText) }}
            />
          </div>
          <a
            className="codeBlockShowMore"
            data-testid="show-more-toggle"
            href="#"
            onClick={(e) => {
              e.preventDefault();
              setCollapsed(false);
            }}
          >
            Show More ({hiddenLineCount} more {hiddenLineCount === 1 ? "line" : "lines"})
          </a>
        </>
      );
    } else {
      // !collapsed, OR collapsed-but-not-truncatable — always show full content
      // for short blocks so a global "collapse all" doesn't blank them out.
      bodyDom = <div className="codeBlockContent">{children}</div>;
    }

    return (
      <>
        <div id={id} className={`codeBlockWrapper${collapsed ? " collapsed" : ""}`}>
          <div className="codeBlockBanner">
            {titleDom}
            {actionsDom}
          </div>
          {bodyDom}
        </div>
        {fullscreen &&
          createPortal(
            <div className="modal" onClick={() => setFullscreen(false)}>
              <div
                className="modalContent fullscreenCodeViewer"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="modalBody">
                  <div className="codeBlockBanner">
                    {titleDom}
                    <div className="codeBlockActions">
                      <button onClick={() => _onCopyToClipboard(content)}>Copy</button>
                      <button onClick={() => setFullscreen(false)}>Close</button>
                    </div>
                  </div>
                  <div className="codeBlockContent">
                    <pre
                      className={`block codeBlock language-${codeBlockLang}`}
                      dangerouslySetInnerHTML={{ __html: _highlightCode(content) }}
                    />
                  </div>
                </div>
              </div>
            </div>,
            document.body,
          )}
      </>
    );
  }

  /**
   * Renders a nested navigation schema inside a block. Enables schema-in-schema
   * composition: the inner schema is parsed and rendered with its own tab scope
   * via a local container ref.
   * @param {Object} props
   * @param {string} props.id - The DOM id for the block wrapper.
   * @param {string} props.schema - The nested schema string to render.
   * @returns {JSX.Element}
   */
  function NavBlock({ id, schema }) {
    const refNestedContainer = useRef(null);
    return (
      <div id={id} ref={refNestedContainer} className="block navBlock">
        <SchemaRender schema={schema} refContainer={refNestedContainer} isNested />
      </div>
    );
  }

  /**
   * Renders a parsed schema into React elements (titles, headers, links, code blocks, tabs, etc.).
   * Top-level Settings controls theme and the persistent TV/touch display preference.
   * Parsed TV metadata drives scoped visibility without removing schema content or replacing focused links.
   * @param {Object} props
   * @param {string} props.schema - The raw schema string to render.
   * @param {React.RefObject} props.refContainer - Ref to the container DOM element.
   * @param {Function} props.onSetViewMode - Callback to switch view modes.
   * @param {boolean} [props.isNested] - True when rendered inside a NavBlock; suppresses
   *   the top-level title chrome (action bar + document.title override) that only makes
   *   sense at the outermost level.
   * @returns {JSX.Element}
   */
  function SchemaRender(props) {
    const { schema, refContainer, onSetViewMode, isNested } = props;
    const [doms, setDoms] = useState(null);
    const hasTvSchema = useRef(false);

    // Apply scope metadata after the parent ref exists, then restore tab selection.
    useLayoutEffect(() => {
      if (refContainer && refContainer.current) {
        const doc = refContainer.current;
        doc.toggleAttribute("data-tv-schema", hasTvSchema.current);

        const tabsList = [...doc.querySelectorAll("tabs")];

        for (const tabs of tabsList) {
          const tabChildren = [...tabs.querySelectorAll("tab")];
          if (tabChildren.length === 0) continue;

          // Restore previously-selected tab from sessionStorage if available;
          // otherwise fall back to selecting the first tab.
          const target = _findRestoredTab(tabs) || tabChildren[0];
          _dispatchEvent(target, "click");
        }
      }
    }, [doms, refContainer.current]);

    // generate the view dom
    useLayoutEffect(() => {
      const serializedSchema = _getSerializedSchema(schema);
      hasTvSchema.current = serializedSchema.some((component) => component.type === "tv_mode");

      const newDoms = serializedSchema.map((schemaComponent) => {
        switch (schemaComponent.type) {
          case "tv_mode":
            return null;
          case "title":
            // Nested nav blocks don't own the page chrome, so drop the title
            // entirely (the parser auto-injects one when missing, which would
            // otherwise render a phantom title + Settings dropdown inside the
            // embedded block).
            if (isNested) {
              return null;
            }

            // set the page title
            document.title = schemaComponent.value;

            return (
              <div
                id={schemaComponent.id}
                key={schemaComponent.key}
                className="title form-search-excluded"
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                {schemaComponent.value}

                <div className="action-bar">
                  {onSetViewMode ? (
                    <>
                      <button id="edit" onClick={() => onSetViewMode("edit")} role="button">
                        Edit
                      </button>
                      <DropdownButtons>
                        <button className="dropdown-trigger">Actions</button>
                        <a role="button" target="_blank" href={NEW_NAV_URL}>
                          New Nav
                        </a>
                        <button
                          className="copyBookmarkToClipboard"
                          onClick={() => _onCopyToClipboard(_getNavBookmarkletFromSchema(schema))}
                        >
                          Copy Bookmark
                        </button>
                        <button onClick={() => onSetViewMode("bookmark_import_chrome")}>
                          Import Chrome Bookmarks
                        </button>
                        <button onClick={() => onSetViewMode("bookmark_export_chrome")}>
                          Export Chrome Bookmarks
                        </button>
                        <a
                          role="button"
                          href={_getUrlDownloadSchema(schema)}
                          download={`schema.${new Date().getTime()}.txt`}
                        >
                          Download Schema
                        </a>
                      </DropdownButtons>
                      <VersionHistoryButton key={Date.now()} {...props} />
                    </>
                  ) : null}

                  <DropdownButtons>
                    <button className="dropdown-trigger">Settings</button>
                    <ThemeToggle />
                    <TvModeToggle />
                  </DropdownButtons>
                </div>
              </div>
            );
          case "favIcon":
            // insert the fav icon
            const pageFavIcon = schemaComponent.value;
            const existingFavIcon = document.querySelector("#pageFavIcon");
            if (existingFavIcon) {
              existingFavIcon.remove();
            }
            const favIconEncoded =
              encodeURIComponent(
                `<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 18 18'><text x='0' y='14'>`,
              ) +
              pageFavIcon +
              encodeURIComponent(`</text></svg>`);
            document.head.insertAdjacentHTML(
              "beforeend",
              `<link id='pageFavIcon' data-fav-icon="${pageFavIcon}" rel="icon" href="data:image/svg+xml,${favIconEncoded}" />`,
            );
            break;
          case "header":
            return (
              <div id={schemaComponent.id} key={schemaComponent.key} className="header">
                {schemaComponent.value}
              </div>
            );
          case "code_block":
            const codeBlockLang = _detectCodeLanguage(schemaComponent.value);
            return (
              <CodeBlockWrapper
                key={schemaComponent.key}
                id={schemaComponent.id}
                title={schemaComponent.tabName || schemaComponent.id || codeBlockLang}
                content={schemaComponent.value}
              >
                <pre
                  className={`block codeBlock language-${codeBlockLang}`}
                  dangerouslySetInnerHTML={{ __html: _highlightCode(schemaComponent.value) }}
                />
              </CodeBlockWrapper>
            );
          case "html_block":
            return (
              <div
                id={schemaComponent.id}
                key={schemaComponent.key}
                className="block htmlBlock"
                dangerouslySetInnerHTML={{ __html: schemaComponent.value }}
              ></div>
            );
          case "nav_block":
            return (
              <NavBlock
                key={schemaComponent.key}
                id={schemaComponent.id}
                schema={schemaComponent.value}
              />
            );
          case "tabs":
            const tabContent = [];
            for (const tab of schemaComponent.tabContent) {
              tabContent.push(
                <tab className="tab" tabIndex="0" data-tab-id={tab.tabId}>
                  {tab.tabName}
                </tab>,
              );
            }
            return (
              <tabs id={schemaComponent.id} key={schemaComponent.key} className="tabs">
                {tabContent}
              </tabs>
            );
          case "link":
            const _onLinkNavigate = (e) => {
              e.currentTarget.classList.add("navigating");
            };
            const _onLinkBlur = (e) => {
              e.currentTarget.classList.remove("navigating");
            };

            switch (schemaComponent.linkType) {
              case "newTabLink":
                return (
                  <a
                    id={schemaComponent.id}
                    key={schemaComponent.key}
                    className="link newTabLink"
                    data-tv-link={schemaComponent.isTvLink || undefined}
                    role="button"
                    target="_blank"
                    href={schemaComponent.linkUrl}
                    data-section={schemaComponent.headerName}
                    onClick={_onLinkNavigate}
                    onBlur={_onLinkBlur}
                  >
                    <FavIcon {...schemaComponent} /> {schemaComponent.linkText}
                  </a>
                );
              case "sameTabLink":
                return (
                  <a
                    id={schemaComponent.id}
                    key={schemaComponent.key}
                    className="link sameTabLink"
                    data-tv-link={schemaComponent.isTvLink || undefined}
                    role="button"
                    href={schemaComponent.linkUrl}
                    data-section={schemaComponent.headerName}
                    onClick={_onLinkNavigate}
                    onBlur={_onLinkBlur}
                  >
                    <FavIcon {...schemaComponent} /> {schemaComponent.linkText}
                  </a>
                );
              case "jsLink":
                return (
                  <button
                    id={schemaComponent.id}
                    key={schemaComponent.key}
                    className="link jsLink"
                    data-tv-link={schemaComponent.isTvLink || undefined}
                    type="button"
                    onClick={(e) => {
                      _onLinkNavigate(e);
                      // Intentional: javascript:// links are a documented schema feature.
                      // linkUrl is a self-contained async IIFE expression (wrapped at parse time),
                      // so it executes globally without needing eval().
                      new Function(schemaComponent.linkUrl)();
                    }}
                    onBlur={_onLinkBlur}
                    data-section={schemaComponent.headerName}
                  >
                    <FavIcon {...schemaComponent} /> {schemaComponent.linkText}
                  </button>
                );
              case "dataLink":
                return (
                  <button
                    id={schemaComponent.id}
                    key={schemaComponent.key}
                    className="link dataLink"
                    data-tv-link={schemaComponent.isTvLink || undefined}
                    type="button"
                    onClick={(e) => {
                      _onLinkNavigate(e);
                      _navigateToDataUrl(schemaComponent.linkUrl);
                    }}
                    onBlur={_onLinkBlur}
                    data-section={schemaComponent.headerName}
                  >
                    <FavIcon {...schemaComponent} /> {schemaComponent.linkText}
                  </button>
                );
            }
            break;
        }
      });

      // update the doms
      setDoms(newDoms);
    }, [schema]);

    return <>{doms}</>;
  }

  // Track whether custom nav-generator language has been registered
  let navGeneratorLanguageRegistered = false;

  /**
   * Registers the custom nav-generator language with Monaco editor,
   * including exact TV directives, TV link delimiters, and light/dark themes.
   * @param {Object} monaco - The Monaco editor instance.
   * @returns {void} Registers language tokens and editor themes once.
   */
  function registerNavGeneratorLanguage(monaco) {
    if (navGeneratorLanguageRegistered) return;
    navGeneratorLanguageRegistered = true;

    monaco.languages.register({ id: "nav-generator" });

    monaco.languages.setMonarchTokensProvider("nav-generator", {
      tokenizer: {
        root: [
          [/^!\s+.*$/, "page-title"],
          [/^\s*\?tv\s*$/, "section-header"],
          [/^#+\s+.*$/, "section-header"],
          [/^>>>.*$/, "tab-definition"],
          [/^(---)([\w]*)$/, ["html-delimiter", "block-id"]],
          [/^(```)([\w]*)$/, ["code-fence", "block-id"]],
          [/^([^|]+?)(\s*\|tv\|\s*)(.+)$/, ["link-label", "separator-same-tab", "url"]],
          [/^(.+?)(\s*\|\|\|\s*)(.+)$/, ["link-label", "separator-new-tab", "url"]],
          [/^(.+?)(\s*\|\s*)(.+)$/, ["link-label", "separator-same-tab", "url"]],
          [/^https?:\/\/[^\s]+$/, "url"],
          [/^www\.[^\s]+$/, "url"],
          [/^[a-zA-Z0-9.-]+\.(com|org|net|io|dev|app|co)[^\s]*$/, "url"],
        ],
      },
    });

    monaco.editor.defineTheme("nav-generator-light", {
      base: "vs",
      inherit: true,
      rules: [
        { token: "page-title", foreground: "0066cc", fontStyle: "bold" },
        { token: "section-header", foreground: "267f99", fontStyle: "bold" },
        { token: "tab-definition", foreground: "7f3b9f", fontStyle: "bold" },
        { token: "html-delimiter", foreground: "e91e63", fontStyle: "bold" },
        { token: "code-fence", foreground: "00897b", fontStyle: "bold" },
        { token: "block-id", foreground: "996600", fontStyle: "italic" },
        { token: "link-label", foreground: "283593", fontStyle: "bold" },
        { token: "separator-new-tab", foreground: "ff6600", fontStyle: "bold" },
        { token: "separator-same-tab", foreground: "008800", fontStyle: "bold" },
        { token: "url", foreground: "0000ff", fontStyle: "underline" },
      ],
      colors: {},
    });

    monaco.editor.defineTheme("nav-generator-dark", {
      base: "vs-dark",
      inherit: true,
      rules: [
        { token: "page-title", foreground: "4fc3f7", fontStyle: "bold" },
        { token: "section-header", foreground: "81c784", fontStyle: "bold" },
        { token: "tab-definition", foreground: "ba68c8", fontStyle: "bold" },
        { token: "html-delimiter", foreground: "ff1744", fontStyle: "bold" },
        { token: "code-fence", foreground: "26c6da", fontStyle: "bold" },
        { token: "block-id", foreground: "ffb74d", fontStyle: "italic" },
        { token: "link-label", foreground: "9fa8da", fontStyle: "bold" },
        { token: "separator-new-tab", foreground: "ff9800", fontStyle: "bold" },
        { token: "separator-same-tab", foreground: "66bb6a", fontStyle: "bold" },
        { token: "url", foreground: "64b5f6", fontStyle: "underline" },
      ],
      colors: {},
    });
  }

  /**
   * Monaco-based schema editor component with theme-aware syntax highlighting.
   * Falls back to BasicTextarea if Monaco fails to load.
   * @param {Object} props
   * @param {string} props.value - The editor content.
   * @param {Function} props.onInput - Callback on content change.
   * @param {Function} props.onBlur - Callback on editor blur.
   * @param {boolean} [props.autoFocus] - Whether to auto-focus the editor.
   * @param {string} props.id - DOM element ID.
   * @param {string} [props.type="nav-generator"] - Editor language type.
   * @param {boolean} [props.readOnly=false] - Whether the editor is read-only.
   * @returns {JSX.Element}
   */
  function SchemaEditor(props) {
    const {
      value,
      onInput,
      onBlur,
      autoFocus,
      id,
      type = "nav-generator",
      readOnly = false,
      ...restProps
    } = props;
    const editorRef = useRef(null);
    const [useFallback] = useState(false);

    const currentTheme = document.documentElement.getAttribute("data-theme");
    const language = type === "html" ? "html" : "nav-generator";
    const editorTheme = currentTheme === "light" ? "nav-generator-light" : "nav-generator-dark";

    // Auto-height calculation based on content
    const lineHeight = 20;
    const padding = 20;
    const lineCount = (value || "").split("\n").length;
    const computedHeight = `${Math.max(200, lineCount * lineHeight + padding)}px`;

    // Watch for theme changes and update editor
    useLayoutEffect(() => {
      const observer = new MutationObserver(() => {
        if (editorRef.current) {
          const theme =
            document.documentElement.getAttribute("data-theme") === "light"
              ? "nav-generator-light"
              : "nav-generator-dark";
          editorRef.current.updateOptions({ theme });
        }
      });

      observer.observe(document.documentElement, {
        attributes: true,
        attributeFilter: ["data-theme"],
      });

      return () => observer.disconnect();
    }, []);

    function handleEditorWillMount(monaco) {
      registerNavGeneratorLanguage(monaco);
    }

    function handleEditorDidMount(editor) {
      editorRef.current = editor;

      editor.onDidBlurEditorText(() => {
        if (onBlur) {
          onBlur({ target: { value: editor.getValue() } });
        }
      });

      if (autoFocus) {
        setTimeout(() => editor.focus(), 100);
      }
    }

    function handleEditorChange(newValue) {
      if (onInput) {
        onInput({ target: { value: newValue } });
      }
    }

    function handleEditorValidation() {
      // no-op, can be extended later
    }

    if (useFallback) {
      return (
        <BasicTextarea
          value={value}
          onInput={onInput}
          onBlur={onBlur}
          autoFocus={autoFocus}
          id={id}
          type={type}
          {...restProps}
        />
      );
    }

    return (
      <div id={id} style={{ width: "100%" }}>
        <Editor
          height={computedHeight}
          language={language}
          value={value || ""}
          theme={editorTheme}
          beforeMount={handleEditorWillMount}
          onMount={handleEditorDidMount}
          onChange={handleEditorChange}
          onValidate={handleEditorValidation}
          options={{
            readOnly,
            minimap: { enabled: false },
            scrollBeyondLastLine: false,
            scrollbar: {
              vertical: "hidden",
              horizontal: "hidden",
              handleMouseWheel: false,
            },
            fontSize: 14,
            fontFamily: 'Menlo, Monaco, "Courier New", monospace',
            lineNumbers: "on",
            wordWrap: "on",
            automaticLayout: true,
            tabSize: 2,
            insertSpaces: true,
            renderWhitespace: "selection",
            contextmenu: true,
            folding: true,
            lineDecorationsWidth: 10,
            lineNumbersMinChars: 4,
          }}
          loading={<div style={{ color: "var(--colorTextMain)" }}>Loading Monaco Editor...</div>}
        />
      </div>
    );
  }

  /**
   * Basic textarea fallback with Tab indent/dedent and Enter auto-indent keyboard shortcuts.
   * Used when Monaco editor is unavailable.
   * @param {Object} props
   * @param {string} props.value - The textarea content.
   * @param {Function} props.onInput - Callback on content change.
   * @param {Function} props.onBlur - Callback on blur.
   * @param {string} props.type - Editor type identifier.
   * @param {boolean} [props.readOnly=false] - Whether the textarea is read-only.
   * @returns {JSX.Element}
   */
  function BasicTextarea(props) {
    const { value, onInput, onBlur, readOnly = false, ...restProps } = props;

    const onInputKeyDown = useCallback(
      (e) => {
        const TAB_INDENT = "  ";
        switch (e.key) {
          case "Tab":
            e.preventDefault();
            if (e.shiftKey === true) {
              _deleteIndentAtCursor(e.target, TAB_INDENT.length);
            } else {
              _insertIndentAtCursor(e.target, TAB_INDENT);
            }
            break;
          case "Enter":
            e.preventDefault();
            _persistTabIndent(e.target);
            break;
        }

        function _insertIndentAtCursor(myField, myValue) {
          let startPos = myField.selectionStart;
          let endPos = myField.selectionEnd;

          if (startPos === endPos) {
            myField.value =
              myField.value.substring(0, startPos) + myValue + myField.value.substring(endPos);
            myField.setSelectionRange(startPos + myValue.length, endPos + myValue.length);
          } else {
            const [lineStart, lineEnd] = _getLineStartEnd(myField, startPos, endPos);
            const [res, newStartPos, newEndPos] = _iterateOverRows(
              myField.value.split("\n"),
              lineStart,
              lineEnd,
              (row) => myValue + row,
            );
            myField.value = res;
            myField.setSelectionRange(newStartPos, newEndPos);
          }
          if (onInput) {
            onInput({ target: myField });
          }
        }

        function _deleteIndentAtCursor(myField, length) {
          let startPos = myField.selectionStart;
          let endPos = myField.selectionEnd;

          if (startPos === endPos) {
            myField.value =
              myField.value.substring(0, startPos - 2) + myField.value.substring(endPos);
            myField.setSelectionRange(startPos - length, endPos - length);
          } else {
            const [lineStart, lineEnd] = _getLineStartEnd(myField, startPos, endPos);
            const [res, newStartPos, newEndPos] = _iterateOverRows(
              myField.value.split("\n"),
              lineStart,
              lineEnd,
              (row) => {
                for (let i = 0; i < row.length; i++) {
                  if (row[i] !== " " || i === length) {
                    return row.substr(i);
                  }
                }
                return row;
              },
            );
            myField.value = res;
            myField.setSelectionRange(newStartPos, newEndPos);
          }
          if (onInput) {
            onInput({ target: myField });
          }
        }

        function _persistTabIndent(myField) {
          try {
            const rows = myField.value.substr(0, myField.selectionStart).split("\n");
            const lastRow = rows[rows.length - 1];
            const lastRowIndent = lastRow.match(/^[ ]+/)[0];
            _insertIndentAtCursor(e.target, "\n" + lastRowIndent);
          } catch {
            _insertIndentAtCursor(e.target, "\n");
          }
        }

        function _iterateOverRows(rows, lineStart, lineEnd, func) {
          let newStartPos,
            newEndPos,
            curCharCount = 0;
          const transformFunc = func || ((row) => row);
          const res = [];
          for (let i = 0; i < rows.length; i++) {
            let row = rows[i];
            if (i >= lineStart && i <= lineEnd) row = transformFunc(row);
            if (i === lineStart) newStartPos = curCharCount;
            if (i === lineEnd) newEndPos = curCharCount + row.length;
            curCharCount += row.length + 1;
            res.push(row);
          }
          return [res.join("\n"), newStartPos, newEndPos];
        }

        function _getLineStartEnd(myField, startPos, endPos) {
          let lineStart = 0,
            lineEnd = 0;
          try {
            lineStart = myField.value.substr(0, startPos).match(/\n/g).length;
          } catch {}
          try {
            lineEnd = myField.value.substr(0, endPos).match(/\n/g).length;
          } catch {}
          return [lineStart, lineEnd];
        }
      },
      [onInput],
    );

    return (
      <textarea
        onKeyDown={readOnly ? undefined : onInputKeyDown}
        value={value}
        onInput={onInput}
        onBlur={onBlur}
        readOnly={readOnly}
        {...restProps}
      ></textarea>
    );
  }

  /**
   * Dropdown menu component. First child is the trigger button, remaining children are menu items.
   * Closes on outside click or Escape key.
   * @param {Object} props
   * @param {string} [props.type=""] - Additional CSS class for dropdown content.
   * @param {React.ReactNode} props.children - Trigger button followed by menu items.
   * @returns {JSX.Element}
   */
  function DropdownButtons(props) {
    const { type = "", children } = props;
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef(null);
    const [triggerButton, ...buttonsElems] = children;

    const toggleDropdown = useCallback(() => {
      setIsOpen((prev) => !prev);
    }, []);

    const closeDropdown = useCallback(() => {
      setIsOpen(false);
    }, []);

    // Close dropdown when clicking outside
    useLayoutEffect(() => {
      const handleClickOutside = (event) => {
        if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
          closeDropdown();
        }
      };

      const handleEscape = (event) => {
        if (event.key === "Escape" && isOpen) {
          closeDropdown();
        }
      };

      if (isOpen) {
        document.addEventListener("mousedown", handleClickOutside);
        document.addEventListener("keydown", handleEscape);
        return () => {
          document.removeEventListener("mousedown", handleClickOutside);
          document.removeEventListener("keydown", handleEscape);
        };
      }
    }, [isOpen, closeDropdown]);

    // Clone trigger button to add onClick handler
    const enhancedTrigger = React.cloneElement(triggerButton, {
      onClick: (e) => {
        e.preventDefault();
        e.stopPropagation();
        toggleDropdown();
        if (triggerButton.props.onClick) {
          triggerButton.props.onClick(e);
        }
      },
      "aria-expanded": isOpen,
      "aria-haspopup": "true",
    });

    // Wrap buttons to close dropdown on click
    const enhancedButtons = React.Children.map(buttonsElems, (child) => {
      if (!child) return null;
      return React.cloneElement(child, {
        onClick: (e) => {
          if (child.props.onClick) {
            child.props.onClick(e);
          }
          // Close dropdown after action
          setTimeout(closeDropdown, 100);
        },
      });
    });

    return (
      <div className="dropdown" ref={dropdownRef}>
        {enhancedTrigger}
        {isOpen && <div className={`dropdown-content ${type}`.trim()}>{enhancedButtons}</div>}
      </div>
    );
  }

  /**
   * Placeholder page for creating a new navigation (not yet implemented).
   * @param {Object} props
   * @param {string} props.schema - The schema string.
   * @returns {JSX.Element}
   */
  function PageCreate(props) {
    const { schema } = props;
    return <>create {schema}</>;
  }

  /**
   * Main application component. Routes between read, edit, create, version history,
   * bookmark import/export, and backup download views.
   * @param {Object} props
   * @param {string} props.schema - The initial navigation schema.
   * @param {string} props.viewMode - The initial view mode ("read", "edit", "create", etc.).
   * @returns {JSX.Element}
   */
  function App(props) {
    const [viewMode, setViewMode] = useState(props.viewMode); // read, edit, create
    // Normalize incoming schema to short-form on first render so authored
    // schemas converge to the canonical syntax. The parser still accepts
    // both forms, but every save round-trips through the same migration.
    const [schema, setSchema] = useState(() => migrateSchemaToShortForm(props.schema));

    // events
    const onSetViewMode = (newView) => setViewMode(newView);
    // Schema replacement (Apply from edit, Restore from version history,
    // Chrome bookmark import, etc.) invalidates any saved tab selection —
    // tab ids and scope keys can shift, so we reset to first-tab defaults.
    // Skip the reset when the new schema is identical to avoid disturbing
    // the user's selection on a no-op Apply.
    const onSetSchema = (newSchema) => {
      const migrated = migrateSchemaToShortForm(newSchema);
      setSchema((prev) => {
        if (prev !== migrated) {
          _clearTabSelectionStore();
        }
        return migrated;
      });
    };

    // effect
    useLayoutEffect(() => {
      switch (viewMode) {
        case "edit":
          document.title = "Edit Navigation";
          break;
        case "create":
          document.title = "New Navigation";
          break;
        case "bookmark_import_chrome":
          document.title = "Import Chrome Bookmarks";
          break;
        case "bookmark_export_chrome":
          document.title = "Export Chrome Bookmarks";
          break;
        case "backup_download":
          document.title = "Backup Download";
          break;
      }
    }, [viewMode]);

    // render the proper views
    const allProps = { schema, onSetSchema, onSetViewMode };
    switch (viewMode) {
      case "read":
        return <PageRead {...allProps} />;
      case "edit":
        return <PageEdit {...allProps} />;
      case "create":
        return <PageCreate {...allProps} />;
      case "version_history":
        return <PageVersionHistory {...allProps} />;
      case "bookmark_import_chrome":
        return <PageChromeBookmarkImport {...allProps} />;
      case "bookmark_export_chrome":
        return <PageChromeBookmarkExport {...allProps} />;
      case "backup_download":
        return <PageBackupDownload {...allProps} />;
    }
  }

  /**
   * Version history page. Loads schema snapshots from IndexedDB and allows restoring previous versions.
   * @param {Object} props
   * @param {string} props.schema - The current schema.
   * @param {Function} props.onSetViewMode - Callback to switch view modes.
   * @param {Function} props.onSetSchema - Callback to update the schema.
   * @returns {JSX.Element}
   */
  function PageVersionHistory(props) {
    const { onSetViewMode, onSetSchema } = props;

    const [versions, setVersions] = useState([]);
    const [selectedDate, setSelectedDate] = useState("");
    const [selectedValue, setSelectedValue] = useState("");

    // Load versions on mount
    useLayoutEffect(() => {
      let mounted = true;

      async function loadVersions() {
        try {
          const allVersions = await getVersions();
          if (mounted) {
            // sort newest first
            const sorted = allVersions.toSorted(
              (a, b) => new Date(b.created_at) - new Date(a.created_at),
            );
            setVersions(sorted);
          }
        } catch {
          console.error("Failed to load versions:", err);
        }
      }

      loadVersions();

      return () => {
        mounted = false;
      };
    }, []);

    // Update selected value when dropdown changes
    const handleSelectChange = (e) => {
      const date = e.target.value;
      setSelectedDate(date);

      const version = versions.find((v) => v.created_at === date);
      setSelectedValue(version ? version.value : "");
    };

    // Apply / Cancel handlers
    const handleApply = () => {
      if (selectedValue) {
        onSetSchema(selectedValue); // update schema
      }
      onSetViewMode("read");
    };

    const handleCancel = () => {
      onSetViewMode("read");
    };

    return (
      <div id="command" className="nav-version-history">
        <div className="title">
          Version History
          <div className="action-bar">
            <select value={selectedDate} onChange={handleSelectChange}>
              <option value="">Select a Version</option>
              {versions.map((v) => (
                <option key={v.created_at} value={v.created_at}>
                  {new Date(v.created_at).toLocaleString()}
                </option>
              ))}
            </select>
            <button id="applyEdit" type="button" role="button" onClick={() => handleApply()}>
              Apply
            </button>
            <button id="cancelEdit" type="button" role="button" onClick={() => handleCancel()}>
              Cancel
            </button>
          </div>
        </div>

        <SchemaEditor
          id="input"
          wrap="soft"
          spellcheck="false"
          value={selectedValue}
          readOnly={true}
        ></SchemaEditor>
      </div>
    );
  }

  /**
   * Converts a nav-generator schema into Chrome bookmark HTML format (NETSCAPE-Bookmark-file-1).
   * @param {string} schema - The navigation schema string.
   * @returns {string} Chrome-compatible bookmark HTML string.
   */
  function parseNavGeneratorToChromeBookmark(schema) {
    // Safety check for empty schema
    if (!schema || typeof schema !== "string" || schema.trim().length === 0) {
      return "<!-- No bookmarks to export -->";
    }

    // Parse the schema to get structured data
    const serializedSchema = _getSerializedSchema(schema);

    // Filter for only title, header, and link types
    const relevantItems = serializedSchema.filter(
      (item) => item.type === "title" || item.type === "header" || item.type === "link",
    );

    if (relevantItems.length === 0) {
      return "<!-- No bookmarks to export -->";
    }

    // Find the title (page title)
    const titleItem = relevantItems.find((item) => item.type === "title");
    const pageTitle = titleItem ? titleItem.value : "Bookmarks";

    // Group links under headers
    const groups = [];
    let currentGroup = null;

    for (const item of relevantItems) {
      if (item.type === "header") {
        // Start a new group
        if (currentGroup && currentGroup.links.length > 0) {
          groups.push(currentGroup);
        }
        currentGroup = {
          name: item.value,
          links: [],
        };
      } else if (item.type === "link") {
        // Add link to current group or create default group
        if (!currentGroup) {
          currentGroup = {
            name: "Bookmarks",
            links: [],
          };
        }
        currentGroup.links.push({
          text: item.linkText,
          url: item.linkUrl,
        });
      }
    }

    // Don't forget the last group
    if (currentGroup && currentGroup.links.length > 0) {
      groups.push(currentGroup);
    }

    // Generate Chrome bookmark HTML
    const timestamp = Math.floor(Date.now() / 1000);
    let html = `<!DOCTYPE NETSCAPE-Bookmark-file-1>
<!-- This is an automatically generated file.
     It will be read and overwritten.
     DO NOT EDIT! -->
<META HTTP-EQUIV="Content-Type" CONTENT="text/html; charset=UTF-8">
<TITLE>${pageTitle}</TITLE>
<H1>${pageTitle}</H1>
<DL><p>
`;

    // Add each group as a folder
    groups.forEach((group) => {
      html += `    <DT><H3 ADD_DATE="${timestamp}" LAST_MODIFIED="${timestamp}">${group.name}</H3>\n`;
      html += `    <DL><p>\n`;

      // Add links in this group
      group.links.forEach((link) => {
        html += `        <DT><A HREF="${link.url}" ADD_DATE="${timestamp}">${link.text}</A>\n`;
      });

      html += `    </DL><p>\n`;
    });

    html += `</DL><p>\n`;

    return html;
  }

  /**
   * Chrome bookmark import page. Accepts HTML bookmark file content and parses it into nav-generator schema.
   * @param {Object} props
   * @param {string} props.schema - The current schema.
   * @param {Function} props.onSetViewMode - Callback to switch view modes.
   * @param {Function} props.onSetSchema - Callback to update the schema.
   * @returns {JSX.Element}
   */
  function PageChromeBookmarkImport(props) {
    const { onSetViewMode, onSetSchema } = props;

    const [htmlInput, setHtmlInput] = useState("");

    // Helper function to parse Chrome bookmarks
    function parseChromeBookmarksFromHTML(htmlString) {
      try {
        // ---- Parse HTML string into DOM ----
        // Clean up common HTML issues from Chrome bookmarks
        let cleanedHtml = htmlString
          .replace(/<!--[\s\S]*?-->/g, "") // Remove HTML comments
          .replace(/<p>/gi, "") // Remove opening <p> tags
          .replace(/<\/p>/gi, ""); // Remove closing </p> tags

        const parser = new DOMParser();
        const doc = parser.parseFromString(cleanedHtml, "text/html");

        // Check for parse errors
        const parserError = doc.querySelector("parsererror");
        if (parserError) {
          console.warn("HTML parsing warning:", parserError.textContent);
          // Try to continue anyway - the DOM might still have usable content
        }

        // Find all top-level DL elements
        const allDLs = doc.querySelectorAll("DL");
        if (allDLs.length === 0) {
          console.error("No DL elements found in HTML");
          return "";
        }

        const rootDLs = Array.from(allDLs).filter((dl) => {
          // Find DLs that are not nested inside another DL
          let parent = dl.parentElement;
          while (parent && parent !== doc.body && parent !== doc.documentElement) {
            if (parent.tagName === "DL") {
              return false; // This DL is nested, skip it
            }
            parent = parent.parentElement;
          }
          return true; // This is a root DL
        });

        if (rootDLs.length === 0) {
          console.error("No root DL elements found");
          return "";
        }

        // Helper function to extract domain name from URL
        function getRootNameFromUrl(url) {
          try {
            // normalize missing protocol
            if (!/^https?:\/\//i.test(url)) {
              url = "http://" + url;
            }

            const u = new URL(url);
            let host = u.hostname.toLowerCase();

            // remove common subdomains
            host = host.replace(/^www\./, "");

            // extract base domain
            const parts = host.split(".");
            if (parts.length >= 2) {
              return parts[parts.length - 2]; // abc.com -> abc
            }

            return parts[0];
          } catch {
            return "link";
          }
        }

        // Recursive function to process DOM elements
        function processElement(element, path = []) {
          const results = [];
          const children = Array.from(element.children);
          const processedDLs = new Set(); // Track which DLs we've already processed

          for (let i = 0; i < children.length; i++) {
            const child = children[i];

            if (child.tagName === "DT") {
              // Check if this DT contains a folder (H3) or a link (A)
              const h3 = child.querySelector(":scope > H3");
              const a = child.querySelector(":scope > A");

              if (h3) {
                // This is a folder
                const folderName = h3.textContent.trim();
                const fullPath = [...path, folderName];

                // Add folder header
                results.push(`\n# ${fullPath.join(" > ")}`);

                // Look for the folder's content DL in two possible locations:
                // 1. As a child of this DT (browser may have moved it here during parsing)
                // 2. As the next sibling of this DT (Chrome's original format)

                let folderDL = null;

                // First, check if DL is a direct child of this DT
                const childDL = child.querySelector(":scope > DL");
                if (childDL) {
                  folderDL = childDL;
                } else {
                  // If not a child, look for it as a sibling
                  let nextSibling = child.nextElementSibling;
                  while (nextSibling) {
                    if (nextSibling.tagName === "DL") {
                      folderDL = nextSibling;
                      break;
                    }
                    if (nextSibling.tagName === "DT") {
                      // Hit another DT, no folder content found
                      break;
                    }
                    nextSibling = nextSibling.nextElementSibling;
                  }
                }

                // Process the folder's content DL if found
                if (folderDL) {
                  processedDLs.add(folderDL); // Mark as processed
                  const folderResults = processElement(folderDL, fullPath);
                  results.push(...folderResults);
                }
              } else if (a) {
                // This is a bookmark link
                const href = a.getAttribute("HREF") || a.href;
                const text = a.textContent?.trim();

                if (href) {
                  if (text && text.length > 0) {
                    results.push(`${text} | ${href}`);
                  } else {
                    const rootName = getRootNameFromUrl(href);
                    results.push(`${rootName} | ${href}`);
                  }
                }
              }
            } else if (child.tagName === "DL") {
              // Only process this DL if it wasn't already processed as folder content
              if (!processedDLs.has(child)) {
                const nestedResults = processElement(child, path);
                results.push(...nestedResults);
              }
            }
            // Ignore <p> tags and other elements
          }

          return results;
        }

        // Process all root DLs
        const allResults = [];
        rootDLs.forEach((rootDL) => {
          const results = processElement(rootDL, []);
          allResults.push(...results);
        });

        return allResults.join("\n").trim();
      } catch (error) {
        console.error("Error parsing Chrome bookmarks:", error);
        throw new Error("Failed to parse bookmarks: " + error.message, {
          cause: error,
        });
      }
    }

    // Apply handler - parse HTML and update schema
    const handleApply = async () => {
      if (htmlInput.trim()) {
        try {
          const parsedSchema = parseChromeBookmarksFromHTML(htmlInput);
          if (parsedSchema && parsedSchema.length > 0) {
            createVersion(parsedSchema);
            onSetSchema(parsedSchema);
          } else {
            await alert("No bookmarks found. Please check the HTML format.");
            return;
          }
        } catch (error) {
          console.error("Error parsing bookmarks:", error);
          await alert("Error parsing bookmarks: " + error.message);
          return;
        }
      }
      onSetViewMode("read");
    };

    // Cancel handler
    const handleCancel = () => {
      onSetViewMode("read");
    };

    return (
      <div id="command" className="nav-chrome-bookmark-import">
        <div className="title">
          Import Chrome Bookmarks
          <div className="action-bar">
            <button id="applyEdit" type="button" role="button" onClick={() => handleApply()}>
              Apply
            </button>
            <button id="cancelEdit" type="button" role="button" onClick={() => handleCancel()}>
              Cancel
            </button>
          </div>
        </div>

        <SchemaEditor
          id="input"
          type="html"
          wrap="soft"
          spellcheck="false"
          value={htmlInput}
          onInput={(e) => setHtmlInput(e.target.value)}
        />
      </div>
    );
  }

  /**
   * Chrome bookmark export page. Converts the current schema to Chrome bookmark HTML and allows download.
   * @param {Object} props
   * @param {string} props.schema - The current schema.
   * @param {Function} props.onSetViewMode - Callback to switch view modes.
   * @returns {JSX.Element}
   */
  function PageChromeBookmarkExport(props) {
    const { schema, onSetViewMode } = props;

    const [htmlOutput, setHtmlOutput] = useState("");

    // Generate the Chrome bookmark HTML on mount
    useLayoutEffect(() => {
      const chromeBookmarkHtml = parseNavGeneratorToChromeBookmark(schema);
      setHtmlOutput(chromeBookmarkHtml);
    }, [schema]);

    // Download handler
    const handleDownload = () => {
      // Generate filename with timestamp
      const now = new Date();
      const year = now.getFullYear();
      const month = String(now.getMonth() + 1).padStart(2, "0");
      const day = String(now.getDate()).padStart(2, "0");
      const hours = String(now.getHours()).padStart(2, "0");
      const minutes = String(now.getMinutes()).padStart(2, "0");
      const seconds = String(now.getSeconds()).padStart(2, "0");
      const filename = `bookmark-${year}-${month}-${day}-${hours}-${minutes}-${seconds}.html`;

      // Create a blob and download link
      const blob = new Blob([htmlOutput], { type: "text/html" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      // Close the view
      onSetViewMode("read");
    };

    // Cancel handler
    const handleCancel = () => {
      onSetViewMode("read");
    };

    return (
      <div id="command" className="nav-chrome-bookmark-export">
        <div className="title">
          Export Chrome Bookmarks
          <div className="action-bar">
            <button
              id="downloadBookmark"
              type="button"
              role="button"
              onClick={() => handleDownload()}
            >
              Download
            </button>
            <button id="cancelExport" type="button" role="button" onClick={() => handleCancel()}>
              Cancel
            </button>
          </div>
        </div>

        <SchemaEditor
          id="output"
          type="html"
          wrap="soft"
          spellcheck="false"
          value={htmlOutput}
          readOnly={true}
        />
      </div>
    );
  }

  /**
   * Backup download page. Allows downloading the current schema as a markdown file.
   * @param {Object} props
   * @param {string} props.schema - The current schema.
   * @param {Function} props.onSetViewMode - Callback to switch view modes.
   * @returns {JSX.Element}
   */
  function PageBackupDownload(props) {
    const { schema, onSetViewMode } = props;

    // Download handler
    const handleDownload = () => {
      // Generate filename with timestamp
      const now = new Date();
      const year = now.getFullYear();
      const month = String(now.getMonth() + 1).padStart(2, "0");
      const day = String(now.getDate()).padStart(2, "0");
      const filename = `nav-generator-${year}-${month}-${day}.md`;

      // Create a blob and download link
      const blob = new Blob([schema], { type: "text/markdown" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      // Close the view
      onSetViewMode("read");
    };

    // Cancel handler
    const handleCancel = () => {
      onSetViewMode("read");
    };

    return (
      <div id="command" className="nav-backup-download">
        <div className="title">
          Backup Download
          <div className="action-bar">
            <button
              id="downloadBackup"
              type="button"
              role="button"
              onClick={() => handleDownload()}
            >
              Download
            </button>
            <button id="cancelBackup" type="button" role="button" onClick={() => handleCancel()}>
              Cancel
            </button>
          </div>
        </div>

        <SchemaEditor
          id="backupOutput"
          type="text"
          wrap="soft"
          spellcheck="false"
          value={schema}
          readOnly={true}
        />
      </div>
    );
  }

  // initialization
  // hooking up extra meta data
  document.head.insertAdjacentHTML(
    "beforeend",
    `
      <meta name="viewport" content="width=device-width, initial-scale=1, user-scalable=no" />
      <meta name="format-detection" content="telephone=no" />
      <meta http-equiv="Cache-Control" content="no-cache" />
      <meta http-equiv="Pragma" content="no-cache" />
      <meta http-equiv="page-enter" content="revealtrans(duration=seconds,transition=num)" />
      <meta http-equiv="page-exit" content="revealtrans(duration=seconds,transition=num)" />
    `.trim(),
  );

  // app level events
  document.addEventListener(
    "keydown",
    /**
     * Dispatches activation, read-view commands, and search shortcuts when search is available.
     * @param {KeyboardEvent} e - Captured document keyboard event.
     * @returns {void} May trigger navigation, editing, dialogs, or code-block expansion.
     */
    (e) => {
      // handling enter and spacebar on focusable div
      const { key } = e;
      const target = e.target;
      const focusedElement = document.activeElement;

      // Global collapse/expand all code blocks: Alt+\ (any platform) or
      // Cmd+\ (macOS). Flips the shared flag and broadcasts to every
      // mounted CodeBlockWrapper. Works regardless of where focus is.
      if (key === "\\" && (e.altKey || e.metaKey) && !e.ctrlKey && !e.shiftKey) {
        _codeBlocksAllCollapsed = !_codeBlocksAllCollapsed;
        document.dispatchEvent(
          new CustomEvent(CODE_BLOCK_COLLAPSE_EVENT, {
            detail: { collapsed: _codeBlocksAllCollapsed },
          }),
        );
        _log(`keyboard: ${_codeBlocksAllCollapsed ? "collapse" : "expand"} all code blocks`);
        e.preventDefault();
        return;
      }

      if (e.target.id === "search") {
        if (e.ctrlKey || e.metaKey || e.shiftKey) {
          if (e.key === "Enter") {
            // with alt enter, triggers google search
            location.href = `https://www.google.com/search?q=${encodeURIComponent(e.target.value)}`;
            return;
          }
        }
      }
      if (key === "Enter" || key === " ") {
        if (
          parseInt(target.tabIndex) === 0 &&
          target.tagName !== "TEXTAREA" &&
          target.tagName !== "INPUT" &&
          target.tagName !== "SELECT"
        ) {
          _dispatchEvent(target, "click");

          e.preventDefault();
          e.stopPropagation();
          return;
        }
      } else if (key === "Escape") {
        if (document.querySelector("#input")) {
          if (document.querySelector("#input") === document.activeElement) {
            document.querySelector("#input").blur();
          }
        }
        if (document.querySelector("#search")) {
          const searchBox = document.querySelector("#search");
          if (searchBox === document.activeElement) {
            // Clear search text
            searchBox.value = "";
            searchBox.dispatchEvent(new Event("input", { bubbles: true }));
            searchBox.blur();
          }
        }
      } else if (
        document.querySelector("#fav") &&
        document.querySelector("#search") !== focusedElement &&
        document.querySelectorAll(".modal").length === 0
      ) {
        // Read-view commands remain available when TV mode removes the search box.
        const searchBox = document.querySelector("#search");

        switch (key) {
          case "f":
          case "?":
          case "/":
          case "t":
            if (searchBox && !e.ctrlKey && !e.altKey && !e.metaKey) {
              searchBox.focus();
              e.preventDefault();
            }
            break;
          case "e":
          case "i":
            _dispatchEvent(document.querySelector("#edit"), "click");
            e.preventDefault();
            break;
          case "c":
            if (!e.ctrlKey && !e.altKey && !e.metaKey) {
              _dispatchEvent(document.querySelectorAll(".copyBookmarkToClipboard")[0], "click");
              e.preventDefault();
            }
            break;
        }
      }
    },
    true,
  );

  // click handling for tab selection
  document.addEventListener(
    "click",
    (e) => {
      const target = e.target;
      if (target.classList.contains("tab")) {
        const tab = target;
        const tabsEl = tab.parentElement;
        const tabChildren = [...tabsEl.querySelectorAll("tab")];

        for (const targetTab of tabChildren) {
          const targetTabId = targetTab.dataset?.tabId;
          const contentEl = targetTabId ? document.getElementById(targetTabId) : null;

          if (tab === targetTab) {
            // Show and select target tab. Clear the inline display instead
            // of hardcoding "block" so each content element uses its own
            // CSS `display` — critical for .navBlock which needs
            // `display: flex` to apply gap/layout. Hardcoding "block" broke
            // the flex layout (and thus the gap between embedded pills)
            // the moment a tab was shown.
            if (contentEl) {
              contentEl.style.display = "";

              // If already selected, toggle expansion
              if (tab.classList.contains("selected")) {
                contentEl.classList.add("expanded");
              }
            }
            tab.classList.add("selected");
          } else {
            // Hide and deselect others
            if (contentEl) {
              contentEl.style.display = "none";
              contentEl.classList.remove("expanded"); // Optional: reset expansion on hide
            }
            targetTab.classList.remove("selected");
          }
        }
        // Persist the selection so a refresh re-selects this tab.
        _saveSelectedTab(tabsEl, tab);
        e.preventDefault();
        e.stopPropagation();
      }
    },
    true,
  );

  document.addEventListener(
    "mousedown",
    (e) => {
      if (e.button == 1) {
        // middle click on button to trigger click
        const target = e.target;
        if (target.tagName === "BUTTON") {
          _dispatchEvent(target, "click");
          e.preventDefault();
        }
      }
    },
    true,
  );

  // Reserved for additional scripts/styles if needed
  await Promise.all([]);

  // find and parse the schema from script
  let inputSchema =
    document.querySelector("[type=schema]")?.innerText?.trim() || _getPersistedBufferSchema() || "";
  let viewMode = "read";

  document.innerHTML = `<div style="text-align: center; margin: 20px; font-size: 20px;">Loading...</div>`;

  let _appRoot = null;

  if (document.querySelector("[type=schema]")) {
    // if schema tag is present let's render it as read
    _log("render via inline <script type=schema>", { length: inputSchema.length });
    _render(); // rerender the dom
  } else if (!window.hasCustomNavBeforeLoad) {
    if (location.search.includes("loadNav")) {
      // will wait for postmessage to populate this
      window.history.pushState("", "", APP_INDEX_URL);
      _setSessionValue("loadNavFromSessionStorage", "1");
      _log("waiting for postMessage (loadNav)");

      const _onHandlePostMessageEvent = (event) => {
        const { type } = event.data;
        const newSchema = event.data.schema;
        if (type === "onViewLinks") {
          try {
            _persistBufferSchema(newSchema);
            inputSchema = newSchema;
            _log("render via postMessage onViewLinks", { length: newSchema?.length ?? 0 });
            _render(); // rerender the dom
          } catch {}
        }
      };
      window.addEventListener("message", _onHandlePostMessageEvent);
    } else if (
      location.search.includes("newNav") ||
      (!isRenderedInDataUrl && !location.href.includes("index.html"))
    ) {
      // render as edit mode for newNav
      window.history.replaceState("", "", APP_INDEX_URL);
      _persistBufferSchema(DEFAULT_SCHEMA_TO_RENDER);
      _setSessionValue("loadNavFromSessionStorage", "1");

      inputSchema = DEFAULT_SCHEMA_TO_RENDER;
      viewMode = "edit";

      _log("render via ?newNav default schema (edit mode)", { length: inputSchema.length });
      _render(); // rerender the dom
    } else if (
      _getSessionValue("loadNavFromSessionStorage") === "1" &&
      location.href.includes(APP_INDEX_URL)
    ) {
      // if this flag is set, then continue
      // will proceed with loading from session storage
      _log("render via sessionStorage continuation", { length: inputSchema.length });
      _render(); // rerender the dom
    }
  } else {
    // else if no schema script or anything other way then we need
    // to listen to the app
    document.addEventListener("DOMContentLoaded", () => {
      // Stale-while-revalidate cache by page URL. If we've rendered this
      // URL before (e.g. synle.github.io/fav/), paint the previously-saved
      // schema immediately so the page feels instant. The NavBeforeLoad
      // dispatch below still fires so the consumer can produce a fresh
      // schema; when renderSchema is called with that, we update the cache
      // and re-render. Disabled for data: URLs and authoring URLs (?newNav,
      // ?loadNav) — see _getNavSchemaCacheKey.
      const cacheKey = _getNavSchemaCacheKey();
      if (cacheKey) {
        const cachedSchema = _readNavSchemaCache(cacheKey);
        if (cachedSchema) {
          inputSchema = cachedSchema;
          _log("render via NavBeforeLoad cache hit (stale)", {
            cacheKey,
            length: cachedSchema.length,
          });
          _render();
        } else {
          _log("cache miss (first load for this url)", { cacheKey });
        }
      }

      _log("dispatching NavBeforeLoad (awaiting consumer renderSchema...)");
      _dispatchCustomEvent(document, "NavBeforeLoad", {
        renderSchema: (newSchema) => {
          const isFreshDifferentFromCache = cacheKey
            ? _getLocalValue(cacheKey) !== newSchema
            : true;
          if (cacheKey) {
            _writeNavSchemaCache(cacheKey, newSchema);
          }
          inputSchema = newSchema;
          _log("render via NavBeforeLoad consumer renderSchema (fresh)", {
            length: newSchema?.length ?? 0,
            cacheKey: cacheKey || "<disabled>",
            changed: isFreshDifferentFromCache,
          });
          _render();
        },
      });
    });
  }

  const THEME_KEY = "theme-mode";
  /**
   * Applies a theme by setting the data-theme attribute on the document element.
   * @param {string} theme - The theme name ("dark" or "light").
   */
  function applyTheme(theme) {
    document.documentElement.setAttribute("data-theme", theme);
  }

  /**
   * Sets and persists the theme to localStorage and applies it.
   * @param {string} theme - The theme name ("dark" or "light").
   */
  function setTheme(theme) {
    applyTheme(theme);
    _setLocalValue(THEME_KEY, theme);
  }

  /**
   * Clears the theme setting, removing the data-theme attribute and localStorage value.
   */
  function clearTheme() {
    document.documentElement.removeAttribute("data-theme");
    _setLocalValue(THEME_KEY, "");
  }

  /**
   * Gets the initial theme from localStorage.
   * @returns {string|null} The saved theme name, or null if unavailable.
   */
  function getInitialTheme() {
    try {
      return _getLocalValue(THEME_KEY);
    } catch {
      return null;
    }
  }

  // Apply theme immediately on page load to prevent flash
  (function initTheme() {
    const savedTheme = getInitialTheme();
    if (savedTheme === "dark" || savedTheme === "light") {
      applyTheme(savedTheme);
    }
  })();

  /**
   * Theme toggle button component. Switches between dark and light modes.
   * @returns {JSX.Element}
   */
  function ThemeToggle() {
    const [theme, setThemeState] = useState(getInitialTheme);

    useLayoutEffect(() => {
      if (theme === "dark" || theme === "light") {
        setTheme(theme);
      } else {
        clearTheme();
      }
    }, [theme]);

    const toggleTheme = () => {
      setThemeState((prev) => (prev === "dark" ? "light" : "dark"));
    };

    return <button onClick={toggleTheme}>{theme === "dark" ? "Light Mode" : "Dark Mode"}</button>;
  }

  /**
   * Conditionally renders a "Version History" button if IndexedDB versions exist.
   * @param {Object} props
   * @param {Function} props.onSetViewMode - Callback to switch view modes.
   * @returns {JSX.Element|null}
   */
  function VersionHistoryButton({ onSetViewMode }) {
    const [hasVersions, setHasVersions] = useState(false);

    useLayoutEffect(() => {
      let mounted = true;

      async function checkVersions() {
        try {
          const versions = await getVersions();
          if (mounted) {
            setHasVersions(versions.length > 0);
          }
        } catch {
          console.error("Failed to check versions:", err);
        }
      }

      checkVersions();
    }, []);

    if (!hasVersions) return null;

    return (
      <button onClick={() => onSetViewMode("version_history")} type="button" role="button">
        Version History
      </button>
    );
  }

  // IndexedDB setup
  const DB_NAME = "VersionsDB";
  const STORE_NAME = "versions";
  const DB_VERSION = 1;

  let db;

  /**
   * Initializes the IndexedDB database, creating the versions object store if needed.
   * @returns {Promise<IDBDatabase>} The opened database instance.
   */
  function initDB() {
    return new Promise((resolve, reject) => {
      try {
        const request = indexedDB.open(DB_NAME, DB_VERSION);

        request.onupgradeneeded = function (event) {
          db = event.target.result;
          if (!db.objectStoreNames.contains(STORE_NAME)) {
            db.createObjectStore(STORE_NAME, { keyPath: "created_at" });
          }
        };

        request.onsuccess = function (event) {
          db = event.target.result;
          resolve(db);
        };

        request.onerror = function (event) {
          console.error("IndexedDB error:", event.target.error);
          reject(event.target.error);
        };
      } catch {
        console.error("Init DB failed:", err);
        reject(err);
      }
    });
  }

  /**
   * Adds a new version snapshot to IndexedDB. Trims input and skips duplicates.
   * @param {string} value - The schema string to save as a version.
   * @returns {Promise<Object|null>} The created version object, or null if skipped.
   */
  async function createVersion(value) {
    try {
      if (!db) await initDB();

      // Trim input
      const finalValue = value.trim();
      if (!finalValue) {
        console.log("Empty value, skipping insert.");
        return null;
      }

      // Check for duplicates
      const existingVersions = await getVersions();
      const isDuplicate = existingVersions.some((v) => v.value === finalValue);
      if (isDuplicate) {
        console.log("Duplicate value detected, skipping insert.");
        return null;
      }

      return new Promise((resolve, reject) => {
        const tx = db.transaction(STORE_NAME, "readwrite");
        const store = tx.objectStore(STORE_NAME);

        const version = {
          value: finalValue,
          created_at: new Date().toISOString(),
        };

        const request = store.add(version);

        request.onsuccess = () => resolve(version);
        request.onerror = (e) => {
          console.error("Add version failed:", e.target.error);
          reject(e.target.error);
        };
      });
    } catch {
      console.error("createVersion failed:", err);
      throw err;
    }
  }

  /**
   * Retrieves all version snapshots from IndexedDB.
   * @returns {Promise<Array<Object>>} Array of version objects with value and created_at.
   */
  async function getVersions() {
    try {
      if (!db) await initDB();

      return new Promise((resolve, reject) => {
        const tx = db.transaction(STORE_NAME, "readonly");
        const store = tx.objectStore(STORE_NAME);
        const request = store.getAll();

        request.onsuccess = () => resolve(request.result);
        request.onerror = (e) => {
          console.error("Get versions failed:", e.target.error);
          reject(e.target.error);
        };
      });
    } catch {
      console.error("getVersions failed:", err);
      throw err;
    }
  }

  /**
   * Renders or re-renders the main App component into the DOM.
   * Creates the app root container on first call.
   */
  function _render() {
    if (!_appRoot) {
      let appContainer = document.getElementById("app-root");
      if (!appContainer) {
        appContainer = document.createElement("div");
        appContainer.id = "app-root";
        document.body.appendChild(appContainer);
      }
      _appRoot = createRoot(appContainer);
    }
    _appRoot.render(
      React.createElement(App, {
        schema: inputSchema,
        viewMode: viewMode,
      }),
    );
  }
})();

console.log("Initializing Nav-Generator", new Date());
